import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import BoldMatches from './BoldMatches';
import TriangleJump from './TriangleJump';
import Logo from '../assets/images/logo.svg';
import KoerberStiftungLogo from '../assets/images/koerber_stiftung.png';
import DsgvoLogo from '../assets/images/dsgvo.png';
import LennartRugenProfile from '../assets/images/lennart_circle_pink.png';
import { AGB_URL, PRIVACY_URL } from '../constants/links';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const getErrorText = (payload, fallbackText) => {
  if (!payload) return fallbackText;
  if (typeof payload === 'string') return payload;
  if (typeof payload?.message === 'string') return payload.message;
  try {
    return JSON.stringify(payload);
  } catch (error) {
    return fallbackText;
  }
};

const getPreSaleQuestionStorageKey = (auditId) => `pre_sale_question_sent_${auditId || 'unknown'}`;
const getPopupQuestionnaireAnsweredStorageKey = (auditId) => `popup_questionnaire_answered_${auditId || 'unknown'}`;

// Progress state enum and parser to normalize/validate server-provided state
const ProgressState = Object.freeze({
  IN_PROGRESS: 'IN_PROGRESS',
  AWAITING_SIGNOFF: 'AWAITING_SIGNOFF',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  MISSING_INFORMATION: 'MISSING_INFORMAION', // Note: backend uses typo 'MISSING_INFORMAION'
  LOITERING: 'LOITERING'
});

const clampProgress = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
};

const PROGRESS_POINT_MAP = [
  { percent: 0.5, minutes: 5 }, // Redaction and pre-processing of pdf
  { percent: 0.99, minutes: 4 } // Actual Audit
];

const normalizeProgressMap = (points) => {
  if (!Array.isArray(points) || points.length === 0) {
    return [{ percent: 0, minutes: 0 }];
  }
  const normalized = points
    .filter((point) => point && Number.isFinite(point.percent) && Number.isFinite(point.minutes))
    .map((point) => ({
      percent: clampProgress(point.percent),
      minutes: Math.max(0, point.minutes)
    }))
    .sort((a, b) => a.minutes - b.minutes);

  if (normalized.length === 0) {
    return [{ percent: 0, minutes: 0 }];
  }

  if (normalized[0].minutes !== 0) {
    return [{ percent: 0, minutes: 0 }, ...normalized];
  }
  return normalized;
};

const NORMALIZED_PROGRESS_MAP = normalizeProgressMap(PROGRESS_POINT_MAP);
const PROGRESS_STAGE_MAP = [...NORMALIZED_PROGRESS_MAP].sort((a, b) => a.percent - b.percent);
const PROGRESS_TIMELINE_MAP = (() => {
  if (!PROGRESS_STAGE_MAP.length) {
    return [{ percent: 0, minutes: 0 }];
  }
  let cumulative = 0;
  const timeline = [{ percent: 0, minutes: 0 }];
  PROGRESS_STAGE_MAP.forEach((stage) => {
    const duration = Number.isFinite(stage.minutes) ? Math.max(0, stage.minutes) : 0;
    cumulative += duration;
    timeline.push({ percent: stage.percent, minutes: cumulative });
  });
  return timeline;
})();

const getProgressFromTimeline = (elapsedMs) => {
  if (!elapsedMs || elapsedMs <= 0) return 0;
  const elapsedMinutes = elapsedMs / 60000;
  const points = PROGRESS_TIMELINE_MAP;

  if (elapsedMinutes <= points[0].minutes) {
    return clampProgress(points[0].percent);
  }

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (elapsedMinutes <= next.minutes) {
      const span = Math.max(0.001, next.minutes - prev.minutes);
      const t = (elapsedMinutes - prev.minutes) / span;
      const interpolated = prev.percent + (next.percent - prev.percent) * t;
      return clampProgress(interpolated);
    }
  }

  return clampProgress(points[points.length - 1].percent);
};

const getMinutesFromProgress = (progress) => {
  const clamped = clampProgress(progress);
  const points = PROGRESS_TIMELINE_MAP;
  if (clamped <= points[0].percent) {
    return points[0].minutes;
  }
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (clamped <= next.percent) {
      const span = Math.max(0.001, next.percent - prev.percent);
      const t = (clamped - prev.percent) / span;
      return prev.minutes + (next.minutes - prev.minutes) * t;
    }
  }
  return points[points.length - 1].minutes;
};

const getNextStageForProgress = (progress) => {
  const clamped = clampProgress(progress);
  const points = PROGRESS_STAGE_MAP;
  if (!points.length) {
    return { lower: { percent: 0, minutes: 0 }, upper: null };
  }
  for (let i = 0; i < points.length; i += 1) {
    if (clamped < points[i].percent) {
      const lower = points[i - 1] ?? { percent: 0, minutes: 0 };
      return { lower, upper: points[i] };
    }
  }
  return { lower: points[points.length - 1], upper: null };
};

const getTotalEstimatedMinutes = () => {
  if (!PROGRESS_TIMELINE_MAP.length) return 0;
  return PROGRESS_TIMELINE_MAP[PROGRESS_TIMELINE_MAP.length - 1]?.minutes ?? 0;
};

const formatRemainingMinutes = (minutes) => {
  if (!Number.isFinite(minutes)) return null;
  if (minutes <= 0.75) return 'weniger als 1 Minute';
  const rounded = Math.round(minutes);
  if (rounded <= 1) return '1 Minute';
  return `${rounded} Minuten`;
};

function parseProgressState(value) {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(ProgressState, normalized)
    ? ProgressState[normalized]
    : null;
}

// Ad Info (buy-offer teaser) helpers
const formatEuro = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  } catch (e) {
    return `€${value.toFixed(2)}`;
  }
};

const getFirstSentenceOrLine = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Keep only the first sentence/line to avoid very long or duplicated strings.
  const firstLine = trimmed.split('\n')[0]?.trim() || '';
  const dotIdx = firstLine.indexOf('.');
  const candidate = (dotIdx >= 0 ? firstLine.slice(0, dotIdx) : firstLine).trim();
  const normalized = candidate.replace(/\s+/g, ' ');
  return normalized || null;
};

const hasWiderspruch = (widerspruch) =>
  Boolean(
    widerspruch &&
      typeof widerspruch === 'object' &&
      (Number.isFinite(Number(widerspruch?.euro_abzug)) || typeof widerspruch?.begruendung === 'string')
  );

const REDACTED_AUDIT_TEXTS = [
  'Die detaillierte Begründung sehen Sie im vollständigen Prüfbericht',
  'Die genaue Begründung sehen Sie im vollständigen Prüfbericht nach dem Kauf',
  'Details zur Heizkostenprüfung werden im vollständigen Bericht angezeigt'
];

const normalizeAuditText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.!]+$/g, '')
    .toLowerCase();

const REDACTED_AUDIT_TEXT_LOOKUP = new Set(REDACTED_AUDIT_TEXTS.map(normalizeAuditText));

const isRedactedAuditText = (value) => REDACTED_AUDIT_TEXT_LOOKUP.has(normalizeAuditText(value));

const REDACTED_UNBLUR_DELAY_MS = 1500;

const RedactedAuditText = ({ text, onClick, forceRedacted = false, linkLabel = 'Klicken Sie hier', linkStyle }) => {
  const shouldRenderRedacted = forceRedacted || isRedactedAuditText(text);
  const rootRef = useRef(null);
  const revealTimeoutRef = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const clearRevealTimeout = () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    };

    if (!shouldRenderRedacted) {
      clearRevealTimeout();
      setIsRevealed(false);
      return undefined;
    }

    const node = rootRef.current;
    if (!node || typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    let isInViewport = false;
    let isDocumentVisible = document.visibilityState === 'visible';

    const setBlurred = () => {
      clearRevealTimeout();
      setIsRevealed(false);
    };

    const scheduleReveal = () => {
      clearRevealTimeout();
      revealTimeoutRef.current = setTimeout(() => {
        revealTimeoutRef.current = null;
        setIsRevealed(true);
      }, REDACTED_UNBLUR_DELAY_MS);
    };

    const syncRevealState = () => {
      if (!isInViewport || !isDocumentVisible) {
        setBlurred();
        return;
      }
      if (!revealTimeoutRef.current) {
        scheduleReveal();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        isInViewport = Boolean(entry?.isIntersecting);
        syncRevealState();
      },
      { threshold: 0.01 }
    );

    const handleVisibilityChange = () => {
      isDocumentVisible = document.visibilityState === 'visible';
      syncRevealState();
    };

    setBlurred();
    observer.observe(node);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearRevealTimeout();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldRenderRedacted, text]);

  if (!shouldRenderRedacted) return text;
  return (
    <span
      ref={rootRef}
      className={`redacted-audit-text${isRevealed ? ' redacted-audit-text--revealed' : ''}`}
    >
      <span className="redacted-audit-text__body">
        {text}{' '}
        <button
          type="button"
          className="redacted-audit-text__link"
          style={linkStyle}
          onClick={(event) => {
            event.stopPropagation();
            if (typeof onClick === 'function') onClick();
          }}
        >
          {linkLabel}
        </button>
      </span>
    </span>
  );
};

const ObjectionEnforcementHint = ({ onClick }) => (
  <div style={{ marginTop: '6px', color: '#7F1D1D', fontSize: '0.78rem', lineHeight: '1.25rem' }}>
    Um den Abzug bzw. die Nichtzahlung durchzusetzen, müssen Sie widersprechen.{' '}
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        if (typeof onClick === 'function') onClick();
      }}
      style={{
        border: 'none',
        padding: 0,
        margin: 0,
        background: 'transparent',
        color: '#111827',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        fontFamily: 'inherit',
        fontWeight: 800,
        textDecoration: 'none',
        cursor: 'pointer'
      }}
    >
      Jetzt widersprechen.
    </button>
  </div>
);

const ClickableObjectionPill = ({ onClick, marginTop = '10px' }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        if (typeof onClick === 'function') onClick();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      aria-label="Jetzt widersprechen"
      style={{
        marginTop,
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: '999px',
        border: '1px solid',
        borderColor: isHovered ? '#F87171' : '#FCA5A5',
        background: isHovered ? '#FECACA' : '#FEE2E2',
        color: isHovered ? '#991B1B' : '#B91C1C',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer',
        transform: isHovered ? 'scale(1.04)' : 'scale(1)',
        boxShadow: isHovered ? '0 10px 18px rgba(185, 28, 28, 0.22)' : '0 2px 6px rgba(185, 28, 28, 0.08)',
        transition: 'transform 0.18s ease, background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease'
      }}
    >
      <span
        style={{
          display: 'inline-grid',
          justifyItems: 'center',
          overflow: 'hidden',
          lineHeight: '1.1'
        }}
      >
        <span
          style={{
            gridArea: '1 / 1',
            whiteSpace: 'nowrap',
            transition: 'transform 0.22s ease, opacity 0.22s ease',
            transform: isHovered ? 'translateY(-120%)' : 'translateY(0)',
            opacity: isHovered ? 0 : 1
          }}
        >
          Durchsetzbar durch Widerspruch
        </span>
        <span
          style={{
            gridArea: '1 / 1',
            whiteSpace: 'nowrap',
            transition: 'transform 0.22s ease, opacity 0.22s ease',
            transform: isHovered ? 'translateY(0)' : 'translateY(120%)',
            opacity: isHovered ? 1 : 0
          }}
        >
          Jetzt widersprechen
        </span>
      </span>
    </button>
  );
};

// Set to a date string (e.g. "12.02.2026, 10:00 Uhr"), or keep null to disable maintenance mode.
const MAINTENANCE_DATE_TEXT = null;

export default function Status() {
  const { auditId } = useParams();
  const query = useQuery();
  const accessKey = query.get('access_key');
  const instantRedirect = (query.get('instant_redirect') || '').toLowerCase() === 'true';
  const origin = (query.get('origin') || '').toLowerCase();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditAdInfo, setAuditAdInfo] = useState(null);
  const [auditAdInfoLoading, setAuditAdInfoLoading] = useState(false);
  const [auditAdInfoError, setAuditAdInfoError] = useState(null);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [questionnaireError, setQuestionnaireError] = useState(null);
  const [questionnaireVisible, setQuestionnaireVisible] = useState(false);
  const [questionnaireSubmitError, setQuestionnaireSubmitError] = useState(null);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  const [questionnaireStepIndex, setQuestionnaireStepIndex] = useState(0);
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false);
  const [popupQuestionnaireData, setPopupQuestionnaireData] = useState(null);
  const [popupQuestionnaireLoading, setPopupQuestionnaireLoading] = useState(false);
  const [popupQuestionnaireError, setPopupQuestionnaireError] = useState(null);
  const [popupQuestionnaireVisible, setPopupQuestionnaireVisible] = useState(false);
  const [popupQuestionnaireSubmitError, setPopupQuestionnaireSubmitError] = useState(null);
  const [popupQuestionnaireAnswers, setPopupQuestionnaireAnswers] = useState({});
  const [popupQuestionnaireStepIndex, setPopupQuestionnaireStepIndex] = useState(0);
  const [popupQuestionnaireCompleted, setPopupQuestionnaireCompleted] = useState(false);
  const [preSaleQuestion, setPreSaleQuestion] = useState('');
  const [preSaleQuestionError, setPreSaleQuestionError] = useState(null);
  const [preSaleQuestionSending, setPreSaleQuestionSending] = useState(false);
  const [hoveredSummaryKey, setHoveredSummaryKey] = useState(null);
  const [hasSubmittedPreSaleQuestion, setHasSubmittedPreSaleQuestion] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (!auditId) return false;
    try {
      return window.localStorage.getItem(getPreSaleQuestionStorageKey(auditId)) === '1';
    } catch (error) {
      return false;
    }
  });
  const [hasAnsweredPopupQuestionnaire, setHasAnsweredPopupQuestionnaire] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (!auditId) return false;
    try {
      return window.localStorage.getItem(getPopupQuestionnaireAnsweredStorageKey(auditId)) === '1';
    } catch (error) {
      return false;
    }
  });
  const [auditReportTab, setAuditReportTab] = useState('summary');
  const [expandedPositionKeys, setExpandedPositionKeys] = useState(new Set());
  const [expandedParentPositionKeys, setExpandedParentPositionKeys] = useState(new Set());
  const intervalRef = useRef(null);
  const auditAdInfoIntervalRef = useRef(null);
  const progressDisplayIntervalRef = useRef(null);
  const progressStageStartRef = useRef(null);
  const progressStageBaseRef = useRef(0);
  const progressStageTargetRef = useRef(null);
  const progressStageDurationRef = useRef(0);
  const displayProgressRef = useRef(0);
  const progressValueRef = useRef(0);
  const questionnaireFetchAbortRef = useRef(null);
  const questionnaireSubmitAbortRef = useRef(null);
  const popupQuestionnaireFetchAbortRef = useRef(null);
  const popupQuestionnaireSubmitAbortRef = useRef(null);
  const popupQuestionnaireTimerRef = useRef(null);
  const popupQuestionnaireInitializedRef = useRef(false);
  const preSaleQuestionSubmitAbortRef = useRef(null);
  const questionnaireDirtyRef = useRef(false);
  const popupQuestionnaireDirtyRef = useRef(false);
  const auditViewedMarkedRef = useRef(false);
  const invalidAuditAdInfoLoggedRef = useRef(false);
  const wasJetztSectionRef = useRef(null);
  const buyButtonSectionRef = useRef(null);
  const [displayProgress, setDisplayProgress] = useState(0);
  const isMaintenanceMode = MAINTENANCE_DATE_TEXT !== null;
  const ESTIMATED_MINUTES = 7;
  const totalEstimatedMinutes = getTotalEstimatedMinutes() || ESTIMATED_MINUTES;
  const remainingMinutes = Math.max(0, totalEstimatedMinutes - getMinutesFromProgress(displayProgress));
  const remainingMinutesText = formatRemainingMinutes(remainingMinutes);
  const remainingMinutesLabel = remainingMinutesText || 'ein paar Minuten';
  const questionnaireQuestions = questionnaireData?.questions || [];
  const popupQuestionnaireQuestions = popupQuestionnaireData?.questions || [];
  const recommendationAction = (() => {
    const recommendation = auditAdInfo?.interruption?.empfehlung_an_mieter || {};
    const handlungTyp = recommendation?.handlung_typ;
    const handlungBetrag = Number(recommendation?.handlung_betrag_euro);

    if (handlungTyp === 'ZAHLEN') {
      if (Number.isFinite(handlungBetrag) && handlungBetrag <= 0) return 'Keine Nachzahlung leisten';
      return 'Nachzahlung leisten';
    }
    if (handlungTyp === 'FORDERN') return 'Guthaben zurückfordern';
    return 'Empfehlung umsetzen';
  })();
  const isAwaitingPayment = progressData?.state === ProgressState.AWAITING_PAYMENT;
  const statusContainerMaxWidth = isAwaitingPayment ? '1080px' : '720px';
  const statusCardMaxWidth = isAwaitingPayment ? '920px' : '500px';
  const scrollToBuyButtonSection = useCallback(() => {
    const targetNode = wasJetztSectionRef.current || buyButtonSectionRef.current;
    if (!targetNode) return;
    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  useEffect(() => {
    setQuestionnaireStepIndex((prev) => Math.min(prev, Math.max(questionnaireQuestions.length - 1, 0)));
  }, [questionnaireQuestions.length]);
  useEffect(() => {
    setPopupQuestionnaireStepIndex((prev) => Math.min(prev, Math.max(popupQuestionnaireQuestions.length - 1, 0)));
  }, [popupQuestionnaireQuestions.length]);

  useEffect(() => {
    const finalModel = auditAdInfo?.interrupting_final_model;
    const reasonsList =
      finalModel?.abrechnung_pruefung?.abrechnung_nicht_akzeptabel_gruende
      ?? finalModel?.abrechnung_nicht_akzeptabel_gruende;
    const hasReasonsTab = Array.isArray(reasonsList)
      && reasonsList.some((item) => item && typeof item === 'object');
    const hasPositionTab = Array.isArray(finalModel?.abrechnung_pruefung?.positionen)
      && finalModel.abrechnung_pruefung.positionen.length > 0;
    const hasHeizkostenTab = Boolean(finalModel?.heizkosten_pruefung && typeof finalModel.heizkosten_pruefung === 'object');
    const hasSummaryTab = hasReasonsTab || hasPositionTab || hasHeizkostenTab;
    const validTabs = [
      hasSummaryTab ? 'summary' : null,
      hasReasonsTab ? 'reasons' : null,
      hasPositionTab ? 'positionen' : null,
      hasHeizkostenTab ? 'heizkosten' : null
    ].filter(Boolean);

    if (validTabs.length === 0) return;
    if (!validTabs.includes(auditReportTab)) {
      setAuditReportTab(validTabs[0]);
    }
  }, [auditAdInfo, auditReportTab]);

  useEffect(() => {
    if (!auditAdInfo) {
      invalidAuditAdInfoLoggedRef.current = false;
      return;
    }

    const hasInterruption = Boolean(auditAdInfo.interruption && typeof auditAdInfo.interruption === 'object');
    const hasFinalModel = Boolean(
      auditAdInfo.interrupting_final_model && typeof auditAdInfo.interrupting_final_model === 'object'
    );

    if (!hasInterruption || !hasFinalModel) {
      if (!invalidAuditAdInfoLoggedRef.current) {
        console.error('Invalid fetchAuditAdInfo payload shape: expected interruption and interrupting_final_model objects.', {
          hasInterruption,
          hasFinalModel,
          payload: auditAdInfo
        });
        invalidAuditAdInfoLoggedRef.current = true;
      }
      return;
    }

    invalidAuditAdInfoLoggedRef.current = false;
  }, [auditAdInfo]);

  useEffect(() => {
    if (!auditId) {
      setHasSubmittedPreSaleQuestion(false);
      setHasAnsweredPopupQuestionnaire(false);
      return;
    }
    try {
      setHasSubmittedPreSaleQuestion(window.localStorage.getItem(getPreSaleQuestionStorageKey(auditId)) === '1');
    } catch (error) {
      setHasSubmittedPreSaleQuestion(false);
    }
    try {
      setHasAnsweredPopupQuestionnaire(
        window.localStorage.getItem(getPopupQuestionnaireAnsweredStorageKey(auditId)) === '1'
      );
    } catch (error) {
      setHasAnsweredPopupQuestionnaire(false);
    }
  }, [auditId]);

  useEffect(() => {
    return () => {
      if (preSaleQuestionSubmitAbortRef.current) {
        preSaleQuestionSubmitAbortRef.current.abort();
      }
    };
  }, []);

  // Add CSS animations for the progress bar and loading indicator
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes rotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #E5E7EB;
        border-top: 4px solid #16a34a;
        border-radius: 50%;
        animation: rotate 1s linear infinite;
        margin: 0 auto;
      }
      .position-click-row {
        transition: background-color 0.22s ease, box-shadow 0.22s ease;
        border-radius: 12px;
      }
      .position-click-row--interactive:hover,
      .position-click-row--expanded {
        background-color: #F9FAFB;
        box-shadow: inset 0 0 0 1px #E5E7EB;
      }
      .position-click-row--objection.position-click-row--interactive:hover,
      .position-click-row--objection.position-click-row--expanded,
      .position-click-row--detail-tease.position-click-row--interactive:hover,
      .position-click-row--detail-tease.position-click-row--expanded {
        background-color: #FEF2F2;
        box-shadow: inset 0 0 0 1px #FCA5A5;
      }
      .position-click-row__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }
      .position-title-tease {
        position: relative;
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        overflow: hidden;
      }
      .position-title-tease__default,
      .position-title-tease__hover {
        transition: transform 0.22s ease, opacity 0.22s ease;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .position-title-tease__default {
        transform: translateY(0);
        opacity: 1;
      }
      .position-title-tease__hover {
        position: absolute;
        left: 0;
        right: 0;
        transform: translateY(120%);
        opacity: 0;
        color: #B91C1C;
        font-weight: 700;
      }
      .position-click-row--interactive:hover .position-title-tease__default {
        transform: translateY(-120%);
        opacity: 0;
      }
      .position-click-row--interactive:hover .position-title-tease__hover {
        transform: translateY(0);
        opacity: 1;
      }
      .position-detail-tease {
        position: relative;
        display: inline-flex;
        min-width: 132px;
        justify-content: center;
        overflow: hidden;
      }
      .position-detail-tease__default,
      .position-detail-tease__hover {
        transition: transform 0.22s ease, opacity 0.22s ease;
      }
      .position-detail-tease__default {
        transform: translateY(0);
        opacity: 1;
      }
      .position-detail-tease__hover {
        position: absolute;
        left: 0;
        right: 0;
        transform: translateY(120%);
        opacity: 0;
      }
      .position-click-row--interactive:hover .position-detail-tease__default {
        transform: translateY(-120%);
        opacity: 0;
      }
      .position-click-row--interactive:hover .position-detail-tease__hover {
        transform: translateY(0);
        opacity: 1;
      }
      .position-expand-link {
        transition: color 0.18s ease;
      }
      .position-expand-link:hover {
        color: #4338CA;
      }
      .redacted-audit-text {
        display: inline;
      }
      .redacted-audit-text__body {
        transition: filter 0.18s ease, opacity 0.18s ease;
        filter: blur(6px);
        opacity: 0.95;
      }
      .redacted-audit-text--revealed .redacted-audit-text__body {
        filter: blur(0);
        opacity: 1;
      }
      .redacted-audit-text__link {
        border: none;
        padding: 0;
        margin: 0;
        background: transparent;
        color: var(--redacted-link-color, currentColor) !important;
        -webkit-text-fill-color: var(--redacted-link-color, currentColor);
        text-decoration: underline;
        text-decoration-color: currentColor;
        text-underline-offset: 2px;
        font-weight: 800;
        font-size: 0.95em;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [questionnaireQuestions.length]);

  // Configurable list of supported payment method logos rendered under the pay button
  const supportedPaymentLogos = [
    // Example: provide paths/URLs to logos you want to display
    // '/path/to/visa.svg', '/path/to/mastercard.svg', '/path/to/amex.svg'
  ];

  const API_BASE_URL = 'https://r2cok49t3g.execute-api.eu-central-1.amazonaws.com/prod';
  const API_KEY = 'xVub93q3wq3dyZ0TgqyC24FwsK53UVbM7FOv171v';
  const QUESTIONNAIRE_ID = 'pre_purchase_v1';
  const POPUP_QUESTIONNAIRE_ID = 'pre_purchase_popup_v1';

  useEffect(() => {
    progressValueRef.current = clampProgress(progressData?.progress);
  }, [progressData?.progress]);

  useEffect(() => {
    displayProgressRef.current = clampProgress(displayProgress);
  }, [displayProgress]);

  useEffect(() => {
    if (progressData?.state !== ProgressState.IN_PROGRESS) {
      progressStageStartRef.current = null;
      progressStageBaseRef.current = 0;
      progressStageTargetRef.current = null;
      progressStageDurationRef.current = 0;
      setDisplayProgress(0);
      if (progressDisplayIntervalRef.current) {
        clearInterval(progressDisplayIntervalRef.current);
        progressDisplayIntervalRef.current = null;
      }
      return;
    }

    if (!progressStageStartRef.current) {
      progressStageStartRef.current = Date.now();
      progressStageBaseRef.current = clampProgress(progressValueRef.current);
    }

    if (!progressDisplayIntervalRef.current) {
      progressDisplayIntervalRef.current = setInterval(() => {
        const serverProgress = progressValueRef.current;
        const { lower, upper } = getNextStageForProgress(serverProgress);

        if (!upper) {
          setDisplayProgress(clampProgress(serverProgress));
          return;
        }

        const targetPercent = upper.percent;
        const stageDurationMinutes = Number.isFinite(upper?.minutes) ? Math.max(0, upper.minutes) : 0;

        const baseLowerBound = Math.max(
          serverProgress,
          Number.isFinite(lower?.percent) ? lower.percent : 0
        );

        const shouldReset =
          progressStageTargetRef.current !== targetPercent ||
          progressStageDurationRef.current !== stageDurationMinutes ||
          baseLowerBound > progressStageBaseRef.current + 0.0001;

        if (shouldReset) {
          progressStageStartRef.current = Date.now();
          progressStageBaseRef.current = baseLowerBound;
          progressStageTargetRef.current = targetPercent;
          progressStageDurationRef.current = stageDurationMinutes;
        }

        const elapsedMs = Date.now() - (progressStageStartRef.current || Date.now());
        const durationMinutes = Math.max(0.001, stageDurationMinutes);
        const t = Math.min(1, (elapsedMs / 60000) / durationMinutes);
        const interpolated = progressStageBaseRef.current + (targetPercent - progressStageBaseRef.current) * t;
        const nextValue = Math.min(
          targetPercent,
          Math.max(displayProgressRef.current || 0, serverProgress, interpolated)
        );

        setDisplayProgress(clampProgress(nextValue));
      }, 750);
    }

    return () => {
      if (progressDisplayIntervalRef.current) {
        clearInterval(progressDisplayIntervalRef.current);
        progressDisplayIntervalRef.current = null;
      }
    };
  }, [progressData?.state]);

  // Function to fetch audit ad info
  const fetchAuditAdInfo = useCallback(async () => {
    try {
      setAuditAdInfoLoading(true);
      setAuditAdInfoError(null);
      
      // // TEMPORARY MOCK - remove after testing
      // const mockResponse = {
      //   is_fully_acceptable: false,
      //   has_contract: true,
      //   tenant_has_paid: false,
      //   price: 49.99,
      //   interruption: {
      //     urteil: 'WIDERSPRUCH',
      //     urteil_begründung: 'Test',
      //     vermieter_verlangt: {
      //       verlangen_typ: 'NACHZAHLUNG',
      //       verlangen_betrag_euro: 100
      //     },
      //     empfehlung_an_mieter: {
      //       handlung_typ: 'ZAHLEN',
      //       handlung_betrag_euro: 5,
      //       handlung_titel: 'test',
      //       kurz_unfo_fuer_email: 'test'
      //   
      //     }
      //   },
      //   interrupting_final_model: {
      //     abrechnung_pruefung: {
      //       abrechnung_nicht_identifiziert: false,
      //       abrechnungszeitraum: '01.01.2024 - 31.12.2024',
      //       vermieter_berechnung: 'Gesamtkosten 5.218,27 EUR minus Vorauszahlungen 4.080,00 EUR ergibt 1.138,27 EUR Nachzahlung.',
      //       positionen: [
      //         {
      //           beschreibung: 'Treppenhausreinigung: 689,22 EUR',
      //           unterpositionen: [
      //             { beschreibung: 'Leistungszeitraum plausibel', widerspruch: null },
      //             {
      //               beschreibung: 'Kostenhoehe auffaellig Kostenhoehe auffaellig Kostenhoehe auffaellig Kostenhoehe auffaellig Kostenhoehe auffaellig Kostenhoehe auffaellig ',
      //               widerspruch: { begruendung: '...', euro_abzug: 5.0}
      //             }
      //           ],
      //           widerspruch: null
      //         },
      //         {
      //           beschreibung: 'Grundsteuer Wohnung: 693,76 EUR',
      //           unterpositionen: null,
      //           widerspruch: null
      //         }
      //       ],
      //       abrechnung_nicht_akzeptabel_gruende: [
      //         {
      //           titel: '...',
      //           begruendung: '...'
      //         }
      //       ]
      //     },
      //     heizkosten_pruefung: {
      //       ergebnis: 'WIDERSPRUCH',
      //       widerspruch: { begruendung: '...', euro_abzug: 5.0}
      //     }
      //   },
      //   kurzinfo_fuer_email:
      //     'Empfehlung: vorerst 0,00 EUR zahlen und die beanstandeten Positionen schriftlich widersprechen.'
      // };
      // setAuditAdInfo(mockResponse);
      // setAuditAdInfoError(null);
      // setAuditAdInfoLoading(false);
      // return;
      // END MOCK

      const url = `${API_BASE_URL}/audits/ad_info/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      
      const raw = response.data || {};
      const interruption =
        raw && typeof raw.interruption === 'object' && raw.interruption !== null ? raw.interruption : null;
      
      const normalizedAuditAdInfo = {
        ...raw,
        is_fully_acceptable: raw?.is_fully_acceptable === true,
        has_contract: raw?.has_contract === true,
        tenant_has_paid: raw?.tenant_has_paid === true,
        interruption
      };
      setAuditAdInfo(normalizedAuditAdInfo);
      setAuditAdInfoError(null);
      // Stop polling once audit ad info is successfully fetched
      if (auditAdInfoIntervalRef.current) {
        clearInterval(auditAdInfoIntervalRef.current);
        auditAdInfoIntervalRef.current = null;
      }
    } catch (err) {
      console.error('Failed to parse fetchAuditAdInfo result:', err);
      setAuditAdInfoError(err?.response?.data || err?.message || 'Unbekannter Fehler beim Laden der Prüfungsinformationen');
      // Stop polling on error - clear the interval
      if (auditAdInfoIntervalRef.current) {
        clearInterval(auditAdInfoIntervalRef.current);
        auditAdInfoIntervalRef.current = null;
      }
    } finally {
      setAuditAdInfoLoading(false);
    }
  }, [API_BASE_URL, API_KEY, auditId, accessKey]);

  const markAuditViewed = useCallback(async () => {
    if (!auditId || !accessKey) return;
    if (origin === 'zentrale') return;

    const params = new URLSearchParams();
    params.set('access_key', accessKey);
    params.set('source', 'frontend');
    if (origin === 'backend') {
      params.set('opened_from_email', 'true');
    }

    const url = `${API_BASE_URL}/audits/${encodeURIComponent(auditId)}/viewed?${params.toString()}`;
    await axios.post(url, null, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }, [API_BASE_URL, auditId, accessKey, origin]);

  const trackSubscriptionPricingOptionClick = useCallback(
    async (source, metadata) => {
      if (!auditId || !accessKey) return;

      const queryParams = new URLSearchParams({ access_key: accessKey });
      const url = `${API_BASE_URL}/audits/${encodeURIComponent(auditId)}/interaction-events?${queryParams.toString()}`;
      const payload = {
        event_type: 'subscription_pricing_option_clicked',
        source: source || 'pricing_section'
      };

      if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
        payload.metadata = metadata;
      }

      try {
        await axios.post(url, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (err) {
        // Tracking should not block checkout interactions.
        console.warn('Failed to track subscription pricing option click:', err);
      }
    },
    [API_BASE_URL, auditId, accessKey]
  );

  const handleQuestionnaireAnswerChange = useCallback((questionIndex, optionIndex, allowMultiSelect) => {
    questionnaireDirtyRef.current = true;
    setQuestionnaireAnswers((prev) => {
      const next = { ...prev };
      const prevOptions = Array.isArray(prev[questionIndex]) ? prev[questionIndex] : [];

      if (allowMultiSelect) {
        const nextSet = new Set(prevOptions);
        if (nextSet.has(optionIndex)) {
          nextSet.delete(optionIndex);
        } else {
          nextSet.add(optionIndex);
        }
        const updated = Array.from(nextSet).sort((a, b) => a - b);
        if (updated.length > 0) {
          next[questionIndex] = updated;
        } else {
          delete next[questionIndex];
        }
        return next;
      }

      next[questionIndex] = [optionIndex];
      return next;
    });

    if (!allowMultiSelect) {
      setQuestionnaireStepIndex((prev) => Math.min(prev + 1, questionnaireQuestions.length - 1));
      if (questionIndex >= questionnaireQuestions.length - 1) {
        setQuestionnaireCompleted(true);
      }
    }
  }, [questionnaireQuestions.length]);

  const handlePopupQuestionnaireAnswerChange = useCallback((questionIndex, optionIndex, allowMultiSelect) => {
    popupQuestionnaireDirtyRef.current = true;
    setPopupQuestionnaireAnswers((prev) => {
      const next = { ...prev };
      const prevOptions = Array.isArray(prev[questionIndex]) ? prev[questionIndex] : [];

      if (allowMultiSelect) {
        const nextSet = new Set(prevOptions);
        if (nextSet.has(optionIndex)) {
          nextSet.delete(optionIndex);
        } else {
          nextSet.add(optionIndex);
        }
        const updated = Array.from(nextSet).sort((a, b) => a - b);
        if (updated.length > 0) {
          next[questionIndex] = updated;
        } else {
          delete next[questionIndex];
        }
        return next;
      }

      next[questionIndex] = [optionIndex];
      return next;
    });

    if (!allowMultiSelect) {
      setPopupQuestionnaireStepIndex((prev) => Math.min(prev + 1, popupQuestionnaireQuestions.length - 1));
      if (questionIndex >= popupQuestionnaireQuestions.length - 1) {
        setPopupQuestionnaireCompleted(true);
      }
    }
  }, [popupQuestionnaireQuestions.length]);

  const submitPreSaleQuestion = useCallback(async () => {
    const trimmedQuestion = preSaleQuestion.trim();
    if (!trimmedQuestion) {
      setPreSaleQuestionError('Bitte geben Sie zuerst eine Frage ein.');
      return;
    }
    if (!auditId || !accessKey) {
      setPreSaleQuestionError('Ihre Sitzung ist unvollständig. Bitte laden Sie die Seite neu.');
      return;
    }

    if (preSaleQuestionSubmitAbortRef.current) {
      preSaleQuestionSubmitAbortRef.current.abort();
    }
    const controller = new AbortController();
    preSaleQuestionSubmitAbortRef.current = controller;

    setPreSaleQuestionSending(true);
    setPreSaleQuestionError(null);

    const queryParams = new URLSearchParams({ access_key: accessKey });
    const url = `${API_BASE_URL}/audits/${encodeURIComponent(auditId)}/pre_sale_question?${queryParams.toString()}`;

    try {
      const response = await axios.post(
        url,
        { pre_sale_question: trimmedQuestion },
        { signal: controller.signal }
      );

      const normalizedQuestion = response?.data?.pre_sale_question;
      if (typeof normalizedQuestion === 'string' && normalizedQuestion.length > 0) {
        setPreSaleQuestion(normalizedQuestion);
      }

      setHasSubmittedPreSaleQuestion(true);
      setQuestionnaireVisible(true);
      try {
        window.localStorage.setItem(getPreSaleQuestionStorageKey(auditId), '1');
      } catch (error) {
        // Ignore storage issues, UI state still controls rendering.
      }
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      if (err?.response?.status === 409) {
        // Question already exists for this audit (write-once endpoint) -> proceed to questionnaire.
        setHasSubmittedPreSaleQuestion(true);
        setQuestionnaireVisible(true);
        try {
          window.localStorage.setItem(getPreSaleQuestionStorageKey(auditId), '1');
        } catch (error) {
          // Ignore storage issues, UI state still controls rendering.
        }
        return;
      }
      setPreSaleQuestionError(
        getErrorText(err?.response?.data, err?.message || 'Unbekannter Fehler beim Senden der Frage')
      );
    } finally {
      setPreSaleQuestionSending(false);
    }
  }, [API_BASE_URL, accessKey, auditId, preSaleQuestion]);

  useEffect(() => {
    if (progressData?.state !== ProgressState.AWAITING_PAYMENT) return;
    if (!auditId) return;
    if (!hasSubmittedPreSaleQuestion) {
      setQuestionnaireLoading(false);
      setQuestionnaireData(null);
      setQuestionnaireError(null);
      setQuestionnaireVisible(false);
      return;
    }

    questionnaireDirtyRef.current = false;
    setQuestionnaireAnswers({});
    setQuestionnaireError(null);
    setQuestionnaireSubmitError(null);
    setQuestionnaireStepIndex(0);
    setQuestionnaireCompleted(false);
    setQuestionnaireVisible(true);

    if (questionnaireFetchAbortRef.current) {
      questionnaireFetchAbortRef.current.abort();
    }

    const controller = new AbortController();
    questionnaireFetchAbortRef.current = controller;
    setQuestionnaireLoading(true);

    const queryParams = new URLSearchParams({ audit_id: auditId });
    if (accessKey) {
      queryParams.set('access_key', accessKey);
    }
    const url = `${API_BASE_URL}/questionnaires/${encodeURIComponent(QUESTIONNAIRE_ID)}?${queryParams.toString()}`;

    let isMounted = true;
    axios
      .get(url, { signal: controller.signal })
      .then((response) => {
        if (!isMounted) return;
        const raw = response?.data || {};
        setQuestionnaireData(raw.questionnaire || null);
        setQuestionnaireError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        if (err?.code === 'ERR_CANCELED') {
          return;
        }
        if (err?.response?.status === 404) {
          setQuestionnaireData(null);
          setQuestionnaireError(null);
          setQuestionnaireVisible(false);
          return;
        }
        setQuestionnaireError(err?.response?.data || err?.message || 'Unbekannter Fehler beim Laden des Fragebogens');
      })
      .finally(() => {
        if (!isMounted) return;
        setQuestionnaireLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [API_BASE_URL, QUESTIONNAIRE_ID, auditId, accessKey, progressData?.state, hasSubmittedPreSaleQuestion]);

  useEffect(() => {
    if (!questionnaireDirtyRef.current) return;
    if (!auditId || !questionnaireData) return;

    if (questionnaireSubmitAbortRef.current) {
      questionnaireSubmitAbortRef.current.abort();
    }

    const controller = new AbortController();
    questionnaireSubmitAbortRef.current = controller;
    setQuestionnaireSubmitError(null);

    const answersPayload = Object.entries(questionnaireAnswers).flatMap(([questionIndex, options]) => {
      const questionIdx = Number(questionIndex);
      if (!Array.isArray(options)) return [];
      return options.map((optionIndex) => ({
        question_index: questionIdx,
        option_index: optionIndex
      }));
    });

    const queryParams = new URLSearchParams({ audit_id: auditId });
    if (accessKey) {
      queryParams.set('access_key', accessKey);
    }
    const url = `${API_BASE_URL}/questionnaires/${encodeURIComponent(QUESTIONNAIRE_ID)}/answers?${queryParams.toString()}`;

    axios
      .post(url, { answers: answersPayload }, { signal: controller.signal })
      .then((response) => {
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED') {
          return;
        }
        setQuestionnaireSubmitError(err?.response?.data || err?.message || 'Unbekannter Fehler beim Speichern der Antworten');
      });

    return () => {
      controller.abort();
    };
  }, [API_BASE_URL, QUESTIONNAIRE_ID, auditId, accessKey, questionnaireData, questionnaireAnswers]);

  useEffect(() => {
    return () => {
      if (popupQuestionnaireFetchAbortRef.current) {
        popupQuestionnaireFetchAbortRef.current.abort();
      }
      if (popupQuestionnaireSubmitAbortRef.current) {
        popupQuestionnaireSubmitAbortRef.current.abort();
      }
      if (popupQuestionnaireTimerRef.current) {
        clearTimeout(popupQuestionnaireTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    popupQuestionnaireInitializedRef.current = false;
    popupQuestionnaireDirtyRef.current = false;
    setPopupQuestionnaireVisible(false);
    setPopupQuestionnaireData(null);
    setPopupQuestionnaireError(null);
    setPopupQuestionnaireSubmitError(null);
    setPopupQuestionnaireAnswers({});
    setPopupQuestionnaireStepIndex(0);
    setPopupQuestionnaireCompleted(false);
    if (popupQuestionnaireFetchAbortRef.current) {
      popupQuestionnaireFetchAbortRef.current.abort();
    }
    if (popupQuestionnaireSubmitAbortRef.current) {
      popupQuestionnaireSubmitAbortRef.current.abort();
    }
    if (popupQuestionnaireTimerRef.current) {
      clearTimeout(popupQuestionnaireTimerRef.current);
      popupQuestionnaireTimerRef.current = null;
    }
  }, [auditId]);

  useEffect(() => {
    if (progressData?.state !== ProgressState.AWAITING_PAYMENT) {
      popupQuestionnaireInitializedRef.current = false;
      popupQuestionnaireDirtyRef.current = false;
      setPopupQuestionnaireVisible(false);
      if (popupQuestionnaireTimerRef.current) {
        clearTimeout(popupQuestionnaireTimerRef.current);
        popupQuestionnaireTimerRef.current = null;
      }
      return;
    }
    if (!auditId) return;
    if (hasAnsweredPopupQuestionnaire) {
      return;
    }
    if (auditAdInfoLoading || auditAdInfoError || !auditAdInfo) {
      console.info('[popup-questionnaire] waiting for ad info', {
        auditId,
        auditAdInfoLoading,
        hasAuditAdInfoError: Boolean(auditAdInfoError),
        hasAuditAdInfo: Boolean(auditAdInfo)
      });
      return;
    }
    if (popupQuestionnaireInitializedRef.current) return;

    console.info('[popup-questionnaire] init fetch', {
      auditId,
      questionnaireId: POPUP_QUESTIONNAIRE_ID
    });

    popupQuestionnaireInitializedRef.current = true;
    popupQuestionnaireDirtyRef.current = false;
    setPopupQuestionnaireLoading(true);
    setPopupQuestionnaireError(null);
    setPopupQuestionnaireSubmitError(null);
    setPopupQuestionnaireAnswers({});
    setPopupQuestionnaireStepIndex(0);
    setPopupQuestionnaireCompleted(false);
    setPopupQuestionnaireVisible(false);

    if (popupQuestionnaireFetchAbortRef.current) {
      popupQuestionnaireFetchAbortRef.current.abort();
    }
    if (popupQuestionnaireTimerRef.current) {
      clearTimeout(popupQuestionnaireTimerRef.current);
      popupQuestionnaireTimerRef.current = null;
    }

    const controller = new AbortController();
    popupQuestionnaireFetchAbortRef.current = controller;

    const queryParams = new URLSearchParams({ audit_id: auditId });
    if (accessKey) {
      queryParams.set('access_key', accessKey);
    }
    const url = `${API_BASE_URL}/questionnaires/${encodeURIComponent(POPUP_QUESTIONNAIRE_ID)}?${queryParams.toString()}`;

    let isMounted = true;
    axios
      .get(url, {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!isMounted) return;
        const raw = response?.data || {};
        const questionnaire =
          (raw?.questionnaire && typeof raw.questionnaire === 'object')
            ? raw.questionnaire
            : (raw && typeof raw === 'object' ? raw : null);
        const questions = Array.isArray(questionnaire?.questions) ? questionnaire.questions : [];
        setPopupQuestionnaireData(questionnaire);
        setPopupQuestionnaireError(null);

        // Show the popup after 20 seconds only when the questionnaire loaded successfully.
        if (questionnaire) {
          console.info('[popup-questionnaire] fetch success, scheduling popup', {
            auditId,
            status: response?.status,
            questionCount: questions.length
          });
          popupQuestionnaireTimerRef.current = setTimeout(() => {
            if (isMounted) {
              console.info('[popup-questionnaire] opening popup now', { auditId });
              setPopupQuestionnaireVisible(true);
            }
          }, 20000);
        } else {
          console.warn('[popup-questionnaire] fetch returned no questionnaire payload', {
            auditId,
            status: response?.status,
            raw
          });
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        if (err?.code === 'ERR_CANCELED') {
          console.info('[popup-questionnaire] fetch canceled', { auditId });
          return;
        }
        if (err?.response?.status === 404) {
          setPopupQuestionnaireData(null);
          setPopupQuestionnaireError(null);
          console.info('[popup-questionnaire] questionnaire not found (404)', {
            auditId,
            questionnaireId: POPUP_QUESTIONNAIRE_ID
          });
          return;
        }
        setPopupQuestionnaireData(null);
        setPopupQuestionnaireError(err?.response?.data || err?.message || 'Unbekannter Fehler beim Laden des Fragebogens');
        console.error('[popup-questionnaire] fetch failed', {
          auditId,
          questionnaireId: POPUP_QUESTIONNAIRE_ID,
          status: err?.response?.status,
          responseData: err?.response?.data,
          message: err?.message
        });
      })
      .finally(() => {
        if (!isMounted) return;
        setPopupQuestionnaireLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
      if (popupQuestionnaireTimerRef.current) {
        clearTimeout(popupQuestionnaireTimerRef.current);
        popupQuestionnaireTimerRef.current = null;
      }
    };
  }, [API_BASE_URL, POPUP_QUESTIONNAIRE_ID, auditId, accessKey, progressData?.state, auditAdInfo, auditAdInfoLoading, auditAdInfoError, hasAnsweredPopupQuestionnaire]);

  useEffect(() => {
    if (!popupQuestionnaireDirtyRef.current) return;
    if (!auditId || !popupQuestionnaireData) return;

    if (popupQuestionnaireSubmitAbortRef.current) {
      popupQuestionnaireSubmitAbortRef.current.abort();
    }

    const controller = new AbortController();
    popupQuestionnaireSubmitAbortRef.current = controller;
    setPopupQuestionnaireSubmitError(null);

    const answersPayload = Object.entries(popupQuestionnaireAnswers).flatMap(([questionIndex, options]) => {
      const questionIdx = Number(questionIndex);
      if (!Array.isArray(options)) return [];
      return options.map((optionIndex) => ({
        question_index: questionIdx,
        option_index: optionIndex
      }));
    });

    const queryParams = new URLSearchParams({ audit_id: auditId });
    if (accessKey) {
      queryParams.set('access_key', accessKey);
    }
    const url = `${API_BASE_URL}/questionnaires/${encodeURIComponent(POPUP_QUESTIONNAIRE_ID)}/answers?${queryParams.toString()}`;

    axios
      .post(url, { answers: answersPayload }, { signal: controller.signal })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED') {
          return;
        }
        setPopupQuestionnaireSubmitError(err?.response?.data || err?.message || 'Unbekannter Fehler beim Speichern der Antworten');
      });

    return () => {
      controller.abort();
    };
  }, [API_BASE_URL, POPUP_QUESTIONNAIRE_ID, auditId, accessKey, popupQuestionnaireData, popupQuestionnaireAnswers]);

  useEffect(() => {
    if (!popupQuestionnaireVisible || !popupQuestionnaireCompleted) return;

    const timeoutId = setTimeout(() => {
      setPopupQuestionnaireVisible(false);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [popupQuestionnaireVisible, popupQuestionnaireCompleted]);

  useEffect(() => {
    if (!popupQuestionnaireCompleted) return;
    if (!auditId) return;
    if (hasAnsweredPopupQuestionnaire) return;

    setHasAnsweredPopupQuestionnaire(true);
    try {
      window.localStorage.setItem(getPopupQuestionnaireAnsweredStorageKey(auditId), '1');
    } catch (error) {
      // Ignore storage errors; in-memory state still prevents showing during this session.
    }
  }, [auditId, popupQuestionnaireCompleted, hasAnsweredPopupQuestionnaire]);

  // Function to refresh status and check if payment was completed
  const refreshStatusAfterPayment = async () => {
    try {
      //// TEMPORARY MOCK - remove after testing
      // const mockResponse = {
      //   state: 'AWAITING_PAYMENT', // Test: 'IN_PROGRESS', 'AWAITING_SIGNOFF', 'AWAITING_PAYMENT', 'COMPLETED', 'FAILED', 'MISSING_INFORMAION'
      //   progress: 0,
      //   error: null,
      //   submission_access_link: null,
      //   payment_link_url: null
      // };
      // const raw = mockResponse || {};
      // const parsedState = parseProgressState(raw.state);
      // const newState = parsedState ?? raw.state ?? null;
      // if (newState === ProgressState.AWAITING_PAYMENT) {
      //   alert('Die Zahlung wurde noch nicht bestätigt. Bitte warten Sie einige Sekunden und aktualisieren Sie die Seite, oder überprüfen Sie, ob die Zahlung erfolgreich abgeschlossen wurde.');
      //   return;
      // }
      // setProgressData({
      //   ...raw,
      //   state: newState
      // });
      // setError(null);
      // return;
      //// END MOCK

      const url = `${API_BASE_URL}/audits/progress/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
      const response = await axios.get(url,
        {
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const raw = response.data || {};
      const parsedState = parseProgressState(raw.state);
      const newState = parsedState ?? raw.state ?? null;
      
      // If still awaiting payment, show alert
      if (newState === ProgressState.AWAITING_PAYMENT) {
        alert('Die Zahlung wurde noch nicht bestätigt. Bitte warten Sie einige Sekunden und aktualisieren Sie die Seite, oder überprüfen Sie, ob die Zahlung erfolgreich abgeschlossen wurde.');
        return;
      }
      
      // Update progress data with new state
      setProgressData({
        ...raw,
        state: newState
      });
      setError(null);
    } catch (err) {
      setError(err?.response?.data || err?.message || 'Unbekannter Fehler beim Aktualisieren des Status');
    }
  };

  useEffect(() => {
    if (isMaintenanceMode) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    // Helper function to check if state is terminal
    const isTerminalState = (state) => {
      return state === ProgressState.COMPLETED ||
             state === ProgressState.FAILED ||
             state === ProgressState.AWAITING_PAYMENT ||
             state === ProgressState.MISSING_INFORMATION ||
             state === ProgressState.LOITERING;
    };

    const fetchProgress = async () => {
      // // TEMPORARY MOCK - remove after testing
      // const mockResponse = {
      //   state: 'IN_PROGRESS', // Test: 'IN_PROGRESS', 'AWAITING_SIGNOFF', 'AWAITING_PAYMENT', 'COMPLETED', 'FAILED', 'MISSING_INFORMAION', 'LOITERING'
      //   progress: 0.0,
      //   error: null, // Test: 'Test error message' or null for internal error'
      //   submission_access_link: null, // Test: 'https://example.com' or null
      //   payment_link_url: "https://www.google.com" // Payment link returned by stripe
      // };
      // if (!isMounted) return;
      // const parsedState = parseProgressState(mockResponse.state);
      // setProgressData({
      //   ...mockResponse,
      //   state: parsedState ?? mockResponse.state ?? null
      // });
      // setError(null);
      // if (isMounted) setLoading(false);
      // return;
      // // END MOCK

      try {
        const url = `${API_BASE_URL}/audits/progress/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
        const response = await axios.get(url,
          {
            headers: {
              Authorization: `Bearer ${API_KEY}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (!isMounted) return;
        const raw = response.data || {};
        // If a redirect link is provided and the audit is not COMPLETED,
        // navigate immediately in the same window. For COMPLETED we show
        // a confirmation message first and redirect shortly after.
        if (raw && raw.submission_access_link && String(raw.state).toUpperCase() !== 'COMPLETED') {
          if (intervalRef.current) clearInterval(intervalRef.current);
          window.location.assign(raw.submission_access_link);
          return;
        }
        const parsedState = parseProgressState(raw.state);
        const newState = parsedState ?? raw.state ?? null;
        setProgressData({
          ...raw,
          // ensure state is a validated enum (or keep original if unknown)
          state: newState
        });
        setError(null);
        
        // Stop polling if we've reached a terminal state
        if (isTerminalState(newState)) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err?.response?.data || err?.message || 'Unbekannter Fehler');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // initial fetch
    fetchProgress();
    
    // Only start polling if current state is not terminal
    // If progressData is null (initial load), we'll start polling and check after first fetch
    if (!isTerminalState(progressData?.state)) {
      // polling every 5 seconds
      intervalRef.current = setInterval(fetchProgress, 5000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (auditAdInfoIntervalRef.current) clearInterval(auditAdInfoIntervalRef.current);
    };
  }, [auditId, accessKey, progressData?.state, isMaintenanceMode]);

  // If a submission_access_link is present, redirect.
  // For COMPLETED, show a short success message first and then redirect.
  useEffect(() => {
    const link = progressData?.submission_access_link;
    const state = progressData?.state;
    if (!link) return;
    if (state === ProgressState.COMPLETED) {
      const timeoutId = setTimeout(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        window.location.assign(link);
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
    window.location.assign(link);
  }, [progressData?.submission_access_link, progressData?.state]);

  // If awaiting payment and instant_redirect=true, go directly to the payment link (if available)
  useEffect(() => {
    if (progressData?.state !== ProgressState.AWAITING_PAYMENT) return;
    if (!instantRedirect) return;
    const paymentLink = progressData?.payment_link_url;
    if (!paymentLink) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    window.location.assign(paymentLink);
  }, [progressData?.state, progressData?.payment_link_url, instantRedirect]);

  // If MISSING_INFORMATION state, redirect to nachreichen page
  useEffect(() => {
    if (progressData?.state !== ProgressState.MISSING_INFORMATION) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    const url = `/nachreichen/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
    window.location.assign(url);
  }, [progressData?.state, auditId, accessKey]);

  // Poll for audit ad info when state is AWAITING_PAYMENT
  useEffect(() => {
    if (progressData?.state !== ProgressState.AWAITING_PAYMENT) {
      // Clear any existing audit ad info polling when not in AWAITING_PAYMENT state
      if (auditAdInfoIntervalRef.current) {
        clearInterval(auditAdInfoIntervalRef.current);
        auditAdInfoIntervalRef.current = null;
      }
      return;
    }

    // Don't start polling if there's already an error - user needs to refresh to retry
    if (auditAdInfoError) {
      return;
    }

    // Don't start polling if audit ad info has already been successfully fetched
    if (auditAdInfo) {
      return;
    }

    // Start polling for audit ad info
    fetchAuditAdInfo();
    auditAdInfoIntervalRef.current = setInterval(fetchAuditAdInfo, 5000);

    return () => {
      if (auditAdInfoIntervalRef.current) {
        clearInterval(auditAdInfoIntervalRef.current);
        auditAdInfoIntervalRef.current = null;
      }
    };
  }, [progressData?.state, auditAdInfoError, auditAdInfo, fetchAuditAdInfo]);

  useEffect(() => {
    if (auditViewedMarkedRef.current) return;
    if (progressData?.state !== ProgressState.AWAITING_PAYMENT) return;
    if (!auditAdInfo) return;

    auditViewedMarkedRef.current = true;
    markAuditViewed().catch(() => {
      // Best-effort tracking; do not block UI if it fails.
    });
  }, [progressData?.state, auditAdInfo, markAuditViewed]);

  // Render only a minimal loading indicator on the very first fetch
  if (loading && !error && !progressData && !isMaintenanceMode) {
    return (
      <div className="status-page-wrapper" style={{ display: 'flex', flexDirection: 'column', minHeight: '100svh' }}>
        <div className="App status-page-app" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px' }}>
            <div style={{ color: '#6B7280' }}>Lade...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="status-page-wrapper" style={{ display: 'flex', flexDirection: 'column', minHeight: '100svh' }}>
      <div className="App status-page-app" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flex: 1, paddingTop: '40px', paddingBottom: '40px' }}>
        <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px 20px', maxWidth: statusContainerMaxWidth, width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '40px' }}>
          <img src={Logo} alt="Nebenkostenprofi Logo" style={{ height: '44px', width: 'auto' }} />
          <div style={{ fontSize: '1rem', color: '#000000' }}>Nebenkostenprofi.de</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
            {isMaintenanceMode
              ? 'Vorübergehend nicht verfügbar'
              : (progressData?.state === ProgressState.AWAITING_PAYMENT ? 'Ergebnis der Prüfung' : 'Status der Prüfung')}
          </div>
        </div>
        
        {loading && !isMaintenanceMode && (
          <div style={{ color: '#6B7280' }}>Lade Fortschritt...</div>
        )}
        
        {!loading && error && !isMaintenanceMode && (
          <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '12px 16px', borderRadius: '8px' }}>
            <strong>Fehler:</strong> {String(error)}
          </div>
        )}

        {isMaintenanceMode && (
          <div style={{ width: '100%', maxWidth: '640px' }}>
            <div
              style={{
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'left'
              }}
            >
              <h3 style={{ margin: '0 0 10px 0', color: '#92400E', fontSize: '1.2rem' }}>
                Wartungsarbeiten
              </h3>
              <p style={{ margin: '0 0 10px 0', color: '#78350F', lineHeight: '1.5' }}>
                Unsere Seite ist aktuell wegen Wartungsarbeiten vorübergehend nicht verfügbar.
              </p>
              <p style={{ margin: 0, color: '#78350F', lineHeight: '1.5' }}>
                Voraussichtlich wieder erreichbar ab: <strong>{MAINTENANCE_DATE_TEXT}</strong>
              </p>
            </div>
          </div>
        )}
        
        {!isMaintenanceMode && !loading && !error && progressData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: statusCardMaxWidth }}>
            {/* Handle FAILED state */}
            {progressData.state === ProgressState.FAILED && (
              <div>
                <div style={{ marginBottom: '12px', color: '#374151', fontSize: '1rem' }}>
                  Bei der Prüfung ist ein Fehler aufgetreten, der vom System nicht behoben werden konnte. Die Prüfung konnte nicht abgeschlossen werden.
                </div>
                <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '16px', borderRadius: '8px' }}>
                  {progressData.error ? (
                    <div>
                      <div style={{ marginBottom: '12px' }}>
                        <strong>Fehler:</strong> {progressData.error}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: '8px' }}>
                        Bitte beheben Sie das Problem mit Ihrer Einreichung und senden Sie diese erneut ein.
                      </div>
                      <button
                        onClick={() => window.open('https://einreichen.nebenkostenprofi.de', '_blank')}
                        style={{
                          background: '#DC2626',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          marginTop: '8px'
                        }}
                      >
                        Hier erneut einreichen
                      </button>
                    </div>
                  ) : (
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Interner Fehler</strong><br />
                      Es ist ein unerwarteter Fehler aufgetreten. Bitte kontaktieren Sie unseren Support.
                    </div>
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent('Fehler bei Prüfung - Support-Anfrage');
                        const body = encodeURIComponent(
                          `Sehr geehrtes Nebenkostenprofi Team,\n\n` +
                          `bei meiner Prüfung ist ein Fehler aufgetreten.\n\n` +
                          `Prüfung: ${auditId}\n` +
                          `Schlüssel: ${accessKey || 'Nicht angegeben'}\n\n` +
                          `Bitte meldet euch bei mir bezüglich einer Lösung des Problems.\n\n` +
                          `Mit freundlichen Grüßen`
                        );
                        window.location.href = `mailto:info@nebenkostenprofi.de?subject=${subject}&body=${body}`;
                      }}
                      style={{
                        background: '#DC2626',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Support kontaktieren
                    </button>
                  </div>
                )}
                </div>
              </div>
            )}
            
            {/* Handle IN_PROGRESS state */}
            {progressData.state === ProgressState.IN_PROGRESS && (
              <div>
                <div style={{ 
                  background: '#F0FDF4', 
                  border: '1px solid #BBF7D0', 
                  padding: '24px 24px 0', 
                  borderRadius: '12px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '360px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    marginBottom: '16px'
                  }}>
                    <div className="loading-spinner"></div>
                  </div>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    color: '#15803d', 
                    fontSize: '1.25rem',
                    fontWeight: '600'
                  }}>
                    Bleiben Sie {remainingMinutesLabel} dran!
                  </h3>
                  <div style={{ 
                    color: '#374151', 
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    marginBottom: '20px'
                  }}>
                    Das System prüft derzeit Ihre Einreichung.
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{ 
                    width: '100%', 
                    background: '#E5E7EB', 
                    borderRadius: '10px', 
                    overflow: 'hidden',
                    marginBottom: '12px',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      width: `${displayProgress * 100}%`, 
                      height: '14px', 
                      background: 'linear-gradient(90deg, #22c55e, #15803d)',
                      borderRadius: '10px',
                      transition: 'width 0.3s ease-in-out',
                      position: 'relative',
                      minWidth: '32px'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                        animation: 'shimmer 2s infinite'
                      }}></div>
                      <div
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: '#ffffff',
                          textShadow: '0 1px 2px rgba(0,0,0,0.25)'
                        }}
                        aria-hidden="true"
                      >
                        {Math.round(displayProgress * 100)}%
                      </div>
                    </div>
                  </div>

                  <div style={{
                    width: 'calc(100% + 48px)',
                    background: 'transparent',
                    borderRadius: 0,
                    border: 'none',
                    padding: 0,
                    marginTop: 'auto',
                    marginBottom: 0,
                    marginLeft: -24,
                    marginRight: -24,
                    textAlign: 'left'
                  }}>
                    <TriangleJump />
                  </div>
                  
                </div>
                <div
                  style={{
                    marginTop: '28px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    width: '100%'
                  }}
                >
                  <img
                    src={KoerberStiftungLogo}
                    alt="Körber Stiftung"
                    style={{ height: '38px', width: 'auto', objectFit: 'contain' }}
                  />
                  <img
                    src={DsgvoLogo}
                    alt="DSGVO"
                    style={{ height: '38px', width: 'auto', objectFit: 'contain' }}
                  />
                  <div
                    style={{
                      fontSize: '0.8rem',
                      lineHeight: '1.15rem',
                      color: '#000000',
                      textAlign: 'center',
                      fontWeight: 700,
                      whiteSpace: 'pre-line'
                    }}
                  >
                    Eingetragener{'\n'}Rechtsdienstleister{'\n'}
                    <span style={{ fontSize: '0.7rem' }}>VII 5-2025 0001 1629</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Handle AWAITING_SIGNOFF state */}
            {progressData.state === ProgressState.AWAITING_SIGNOFF && (
              <div>
                <div style={{ 
                  background: '#F0F9FF', 
                  border: '1px solid #BAE6FD', 
                  padding: '24px', 
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    fontSize: '48px', 
                    marginBottom: '16px',
                    color: '#0369A1'
                  }}>
                    🔍
                  </div>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    color: '#0C4A6E', 
                    fontSize: '1.25rem',
                    fontWeight: '600'
                  }}>
                    Qualitätskontrolle läuft
                  </h3>
                  <div style={{ 
                    color: '#374151', 
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    marginBottom: '16px'
                  }}>
                    Ihre Prüfung wurde zufällig für eine zusätzliche Qualitätskontrolle ausgewählt. 
                    Unser Team nimmt sich Zeit für eine gründliche Überprüfung Ihrer Einreichung.
                  </div>
                  <div style={{ 
                    color: '#6B7280', 
                    fontSize: '0.9rem',
                    fontStyle: 'italic'
                  }}>
                    Sie erhalten eine E-Mail, sobald die Prüfung abgeschlossen ist. 
                    Bitte haben Sie etwas Geduld.
                  </div>
                </div>
              </div>
            )}
            
            {/* Handle AWAITING_PAYMENT state */}
            {progressData.state === ProgressState.AWAITING_PAYMENT && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', width: '100%' }}>
                {(() => {
                  const interruption = auditAdInfo?.interruption;
                  const isFullyAcceptable = auditAdInfo?.is_fully_acceptable === true && !interruption;

                  return (
                    <>
                    </>
                  );
                })()}

                {(() => {
                  const interruption = auditAdInfo?.interruption;
                  if (!interruption) return null;

                  const landlord = interruption?.vermieter_verlangt;
                  const landlordType = String(landlord?.verlangen_typ || '').toUpperCase();
                  const landlordAmount = Number(landlord?.verlangen_betrag_euro);
                  const landlordHeading =
                    landlordType === 'NACHZAHLUNG'
                      ? 'Nachzahlung'
                      : landlordType === 'GUTHABEN' || landlordType === 'RUECKZAHLUNG'
                        ? 'Guthaben'
                        : 'Betrag';
                  const landlordAmountLabel = Number.isFinite(landlordAmount) ? formatEuro(landlordAmount) : '—';

                  const recommendation = interruption?.empfehlung_an_mieter || {};
                  const recommendationType = String(recommendation?.handlung_typ || '').toUpperCase();
                  const recommendationAmount = Number(recommendation?.handlung_betrag_euro);
                  const recommendationHeading =
                    recommendationType === 'ZAHLEN'
                      ? Number.isFinite(recommendationAmount) && recommendationAmount <= 0
                        ? 'Keine Nachzahlung'
                        : 'Zahlung'
                      : recommendationType === 'FORDERN'
                        ? 'Rückforderung'
                        : 'Empfehlung';
                  const recommendationAmountLabel = Number.isFinite(recommendationAmount) ? formatEuro(recommendationAmount) : '—';

                  return (
                    <div
                      style={{
                        marginTop: '-10px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: '12px'
                      }}
                    >
                      <div
                        style={{
                          borderRadius: '24px',
                          border: '1px solid #D1D5DB',
                          background: '#F3F4F6',
                          padding: '20px 18px',
                          textAlign: 'left'
                        }}
                      >
                        <div
                          style={{
                            color: '#374151',
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Ihr Vermieter verlangt
                        </div>
                        <div style={{ marginTop: '14px', color: '#1F2937', fontSize: '1.5rem', lineHeight: '1.9rem', fontWeight: 800 }}>
                          {landlordHeading}
                        </div>
                        <div style={{ marginTop: '8px', color: '#0F172A', fontSize: '2.15rem', lineHeight: '2.45rem', fontWeight: 900 }}>
                          {landlordAmountLabel}
                        </div>
                      </div>

                      <div
                        style={{
                          borderRadius: '24px',
                          border: '1px solid #FECACA',
                          background: '#FEF2F2',
                          padding: '20px 18px',
                          textAlign: 'left'
                        }}
                      >
                        <div
                          style={{
                            color: '#374151',
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Wir empfehlen
                        </div>
                        <div style={{ marginTop: '14px', color: '#B91C1C', fontSize: '1.5rem', lineHeight: '1.9rem', fontWeight: 800 }}>
                          {recommendationHeading}
                        </div>
                        <div style={{ marginTop: '8px', color: '#991B1B', fontSize: '2.15rem', lineHeight: '2.45rem', fontWeight: 900 }}>
                          {recommendationAmountLabel}
                        </div>
                        <ClickableObjectionPill onClick={scrollToBuyButtonSection} />
                      </div>
                    </div>
                  );
                })()}
                
                {/* Loading state for audit ad info */}
                {auditAdInfoLoading && (
                  <div style={{ textAlign: 'center' }}>
                    <div className="loading-spinner" style={{ marginBottom: '12px' }}></div>
                    <div style={{ color: '#6B7280', fontSize: '0.9rem' }}>
                      Lade Prüfungsergebnisse...
                    </div>
                  </div>
                )}
                
                {/* Error state for audit ad info */}
                {auditAdInfoError && (
                  <div style={{ 
                    color: '#B91C1C', 
                    background: '#FEF2F2', 
                    border: '1px solid #FECACA', 
                    padding: '12px 16px', 
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                  }}>
                    <strong>Hinweis:</strong> {String(auditAdInfoError)}
                  </div>
                )}

                {/* Email-like preview + CTA */}
                <div style={{ borderRadius: '16px', border: '1px solid rgb(229,229,229)', overflow: 'hidden', backgroundColor: 'rgb(255,255,255)' }}>
                  {/* Prüfbericht Vorschau */}
                  {(() => {
                    const interruption = auditAdInfo?.interruption;
                    const finalModel = auditAdInfo?.interrupting_final_model;

                    const isFullyAcceptable = auditAdInfo?.is_fully_acceptable === true && !interruption;
                    const isMissingContract = auditAdInfo?.has_contract === false;

                    if (isFullyAcceptable) {
                      return (
                        <div style={{ padding: '18px 18px', backgroundColor: isMissingContract ? '#FFFBEB' : '#ECFDF5' }}>
                          <div style={{ fontSize: '0.8rem', lineHeight: '1.2rem', color: isMissingContract ? '#B45309' : '#047857', fontWeight: 800, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                            Prüfbericht
                          </div>
                          <div style={{ marginTop: '8px', fontSize: '1.2rem', lineHeight: '1.55rem', color: isMissingContract ? '#92400E' : '#065F46', fontWeight: 900 }}>
                            {isMissingContract
                              ? 'Ihre Abrechnung ist vorläufig akzeptabel, aber ohne Mietvertrag nicht vollständig prüfbar.'
                              : 'Ihre Abrechnung ist vollständig akzeptabel.'}
                          </div>
                          <div style={{ marginTop: '10px', fontSize: '0.95rem', lineHeight: '1.4rem', color: isMissingContract ? '#92400E' : '#047857' }}>
                            {isMissingContract ? (
                              <>
                                Ohne den Mietvertrag in die Prüfung einschließen zu können sehen wir keine Unzulässigkeiten. Der Mietvertrag ist für die Pruefung
                                jedoch besonders wichtig. Wir empfehlen Ihnen Ihre Unterlagen erneut mit Mietvertrag unter{' '}
                                <a
                                  href="https://einreichen.nebenkostenprofi.de"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#92400E', fontWeight: 800, textDecoration: 'underline' }}
                                >
                                  hier
                                </a>{' '}
                                einzureichen.
                              </>
                            ) : (
                              <>Im vollständigen Prüfbericht sehen Sie die genaue Begründung, Detailprüfung und konkrete Handlungsempfehlungen.</>
                            )}
                          </div>
                        </div>
                      );
                    }

                    if (!interruption || !finalModel) {
                      return (
                        <div style={{ padding: '18px 18px', borderTop: '1px solid rgb(229,229,229)', backgroundColor: 'rgb(255,255,255)' }}>
                          <div
                            style={{
                              borderRadius: '12px',
                              border: '1px solid #FECACA',
                              background: '#FEF2F2',
                              padding: '14px',
                              textAlign: 'left'
                            }}
                          >
                            <div style={{ fontSize: '0.95rem', color: '#991B1B', fontWeight: 800, marginBottom: '6px' }}>
                              Prüfbericht konnte nicht geladen werden
                            </div>
                            <div style={{ fontSize: '0.92rem', lineHeight: '1.45rem', color: '#7F1D1D' }}>
                              Bei der Verarbeitung der Prüfdaten ist ein Fehler aufgetreten. Bitte kontaktieren Sie den Support unter{' '}
                              <a href="mailto:info@nebenkostenprofi.de" style={{ color: '#991B1B', fontWeight: 700 }}>
                                info@nebenkostenprofi.de
                              </a>.
                            </div>
                          </div>
                        </div>
                      );
                    }

                    const reasonsSource =
                      finalModel?.abrechnung_pruefung?.abrechnung_nicht_akzeptabel_gruende
                      ?? finalModel?.abrechnung_nicht_akzeptabel_gruende;
                    const reasons = Array.isArray(reasonsSource)
                      ? reasonsSource.filter((item) => item && typeof item === 'object')
                      : [];

                    const positionen = Array.isArray(finalModel?.abrechnung_pruefung?.positionen)
                      ? finalModel.abrechnung_pruefung.positionen
                      : [];

                    const heizkosten = finalModel?.heizkosten_pruefung;
                    const hasHeizkosten = Boolean(heizkosten && typeof heizkosten === 'object');
                    const heizkostenErgebnis = String(heizkosten?.ergebnis || '').toUpperCase();
                    const isHeizkostenAccepted =
                      heizkostenErgebnis === 'ABRECHNUNG_AKZEPTABEL' || heizkostenErgebnis === 'AKZEPTABEL';
                    const isHeizkostenMissing = heizkostenErgebnis === 'ABRECHNUNG_NICHT_VORHANDEN';
                    const heizkostenIssue =
                      heizkostenErgebnis === 'WIDERSPRUCH' || hasWiderspruch(heizkosten?.widerspruch);

                    const periodDirect =
                      finalModel?.abrechnungszeitraum ||
                      finalModel?.abrechnung_pruefung?.abrechnungszeitraum ||
                      interruption?.abrechnungszeitraum ||
                      null;
                    const periodStart =
                      finalModel?.abrechnungszeitraum_von ||
                      finalModel?.abrechnung_pruefung?.abrechnungszeitraum_von ||
                      interruption?.abrechnungszeitraum_von ||
                      null;
                    const periodEnd =
                      finalModel?.abrechnungszeitraum_bis ||
                      finalModel?.abrechnung_pruefung?.abrechnungszeitraum_bis ||
                      interruption?.abrechnungszeitraum_bis ||
                      null;
                    const periodLabel = periodDirect || (periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : 'Nicht angegeben');

                    const formatIssueBadge = (widerspruch) => {
                      if (!hasWiderspruch(widerspruch)) return null;
                      const euroAbzug = Number(widerspruch?.euro_abzug);
                      if (Number.isFinite(euroAbzug)) {
                        return `Nicht ok · Abzug ${formatEuro(euroAbzug)}`;
                      }
                      return 'Nicht ok';
                    };

                    const getReasonBody = (reason) =>
                      getFirstSentenceOrLine(reason?.begruendung) ||
                      getFirstSentenceOrLine(reason?.beschreibung) ||
                      getFirstSentenceOrLine(reason?.text) ||
                      'Die genaue Begründung sehen Sie im vollständigen Prüfbericht nach dem Kauf.';

                    const getReasonTitle = (reason) =>
                      getFirstSentenceOrLine(reason?.titel) ||
                      getFirstSentenceOrLine(reason?.title) ||
                      null;

                    const getReasonAbzug = (reason) => {
                      const euroAbzug = Number(reason?.widerspruch?.euro_abzug);
                      if (Number.isFinite(euroAbzug)) return formatEuro(euroAbzug);
                      return (
                        getFirstSentenceOrLine(reason?.widerspruch?.begruendung) ||
                        getFirstSentenceOrLine(reason?.abzug_text) ||
                        'Dieser Mangel begründet die vollständige Ablehnung der Abrechnung.'
                      );
                    };

                    const getPositionAbzugSum = (items) => {
                      if (!Array.isArray(items) || items.length === 0) return 0;
                      return items.reduce((sum, position) => {
                        const ownAbzug = Number(position?.widerspruch?.euro_abzug);
                        const ownAbzugValue = Number.isFinite(ownAbzug) ? ownAbzug : 0;
                        const children = Array.isArray(position?.unterpositionen) ? position.unterpositionen : [];
                        return sum + ownAbzugValue + getPositionAbzugSum(children);
                      }, 0);
                    };

                    const renderPositionen = (items, parentIndices = []) =>
                      items.map((position, index) => {
                        const indices = [...parentIndices, index + 1];
                        const numberLabel = `${indices.join('.')}.`;
                        const key = indices.join('-');
                        const isLastItem = index === items.length - 1;
                        const isSubPosition = parentIndices.length > 0;
                        const showBottomBorder = !(isSubPosition && isLastItem);
                        const children = Array.isArray(position?.unterpositionen) ? position.unterpositionen : [];
                        const beschreibung = String(position?.beschreibung || 'Unbenannte Position');
                        const issueLabel = formatIssueBadge(position?.widerspruch);
                        const objectionReason =
                          getFirstSentenceOrLine(position?.widerspruch?.begruendung) ||
                          getFirstSentenceOrLine(position?.widerspruch?.beschreibung) ||
                          'Die detaillierte Begründung sehen Sie im vollständigen Prüfbericht.';
                        const objectionAbzug = (() => {
                          const euroAbzug = Number(position?.widerspruch?.euro_abzug);
                          if (Number.isFinite(euroAbzug)) return formatEuro(euroAbzug);
                          return getFirstSentenceOrLine(position?.widerspruch?.abzug_text) || '—';
                        })();
                        const isObjection = Boolean(issueLabel);
                        const hasSubPositionObjection = !isObjection && hasAnyPositionObjection(children);
                        const statusVariant = isObjection ? 'objection' : hasSubPositionObjection ? 'partial' : 'accepted';
                        const descendantAbzugSum = !isObjection ? getPositionAbzugSum(children) : 0;
                        const statusLabel =
                          statusVariant === 'objection'
                            ? 'Beanstandet'
                            : statusVariant === 'partial'
                              ? 'Teilweise beanstandet'
                              : 'Akzeptiert';
                        const isParent = children.length > 0;
                        const isParentExpanded = expandedParentPositionKeys.has(key);
                        const isDetailsExpanded = expandedPositionKeys.has(key) && !isParentExpanded;
                        const statusTone =
                          statusVariant === 'objection'
                            ? { bg: '#F8F1F1', heading: '#BE3B33', text: '#A4362D' }
                            : statusVariant === 'partial'
                              ? { bg: '#FCEEEE', heading: '#BE3B33', text: '#A4362D' }
                              : { bg: '#F0FDF4', heading: '#166534', text: '#166534' };
                        const detailReason = isObjection
                          ? objectionReason
                          : hasSubPositionObjection
                            ? 'Mindestens eine Unterposition wird beanstandet. Diese Position ist daher teilweise beanstandet.'
                            : 'Diese Position wurde in der Prüfung akzeptiert.';
                        const childLabelExpand = `${children.length} Unterposition${children.length === 1 ? '' : 'en'} ausklappen`;
                        const childLabelCollapse = `${children.length} Unterposition${children.length === 1 ? '' : 'en'} einklappen`;
                        const partialIssueLabel =
                          descendantAbzugSum > 0 ? `Nicht ok · Abzug ${formatEuro(descendantAbzugSum)}` : 'Teilweise beanstandet';
                        const showIssuePill =
                          Boolean(issueLabel) || (statusVariant === 'partial' && (!isParent || !isParentExpanded));
                        const canExpandDetails = statusVariant !== 'accepted';
                        const shouldShowDetailHoverTease =
                          canExpandDetails &&
                          !isDetailsExpanded &&
                          (statusVariant === 'objection' || (isParent && hasSubPositionObjection));
                        const detailInset = indices.length > 1 ? '44px' : '32px';
                        const subPositionCollapseStyle = {
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          background: 'transparent',
                          color: '#6B7280',
                          fontSize: '0.88rem',
                          lineHeight: '1.3rem',
                          fontWeight: 500,
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          cursor: 'pointer'
                        };
                        const subPositionExpandStyle = {
                          border: '1px solid #D1D5DB',
                          padding: '6px 12px',
                          margin: 0,
                          borderRadius: '8px',
                          background: '#F9FAFB',
                          color: '#111827',
                          fontSize: '0.88rem',
                          lineHeight: '1.3rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        };
                        const toggleSubPositions = (event) => {
                          event.stopPropagation();
                          setExpandedParentPositionKeys((prev) => {
                            const next = new Set(prev);
                            const willExpand = !next.has(key);
                            if (next.has(key)) {
                              next.delete(key);
                            } else {
                              next.add(key);
                            }
                            if (willExpand) {
                              setExpandedPositionKeys((prevDetails) => {
                                const nextDetails = new Set(prevDetails);
                                nextDetails.delete(key);
                                return nextDetails;
                              });
                            }
                            return next;
                          });
                        };

                        return (
                          <div key={key} style={{ margin: '6px 0', borderBottom: showBottomBorder ? '1px solid #E5E7EB' : 'none' }}>
                            <div
                              className={`position-click-row${canExpandDetails ? ' position-click-row--interactive' : ''}${
                                statusVariant === 'objection' ? ' position-click-row--objection' : ''
                              }${
                                shouldShowDetailHoverTease ? ' position-click-row--detail-tease' : ''
                              }${
                                isDetailsExpanded ? ' position-click-row--expanded' : ''
                              }`}
                              style={{
                                padding: '10px 8px',
                                cursor: canExpandDetails ? 'pointer' : 'default'
                              }}
                              onClick={() => {
                                if (!canExpandDetails) return;
                                setExpandedPositionKeys((prev) => {
                                  if (prev.has(key)) {
                                    return new Set();
                                  }
                                  return new Set([key]);
                                });
                              }}
                            >
                              <div className="position-click-row__header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, textAlign: 'left' }}>
                                  <div style={{ width: `${indices.length > 1 ? 34 : 22}px`, color: '#111827', fontWeight: 700, flexShrink: 0 }}>
                                    {numberLabel}
                                  </div>
                                  <div
                                    style={{
                                      color: '#1F2937',
                                      fontWeight: 500,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}
                                  >
                                    {shouldShowDetailHoverTease ? (
                                      <span className="position-title-tease">
                                        <span className="position-title-tease__default">{beschreibung}</span>
                                        <span className="position-title-tease__hover">Klicken für Details</span>
                                      </span>
                                    ) : (
                                      beschreibung
                                    )}
                                  </div>
                                </div>
                                {showIssuePill && !isDetailsExpanded && (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '4px 10px',
                                      borderRadius: '9999px',
                                      backgroundColor: statusVariant === 'objection' ? '#FEE2E2' : '#FEE2E2',
                                      color: statusVariant === 'objection' ? '#B91C1C' : '#B91C1C',
                                      fontSize: '0.78rem',
                                      fontWeight: 800,
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {issueLabel || partialIssueLabel}
                                  </span>
                                )}
                              </div>

                              {isDetailsExpanded && (
                                <div
                                  style={{
                                    margin: '10px 0 4px',
                                    marginLeft: detailInset,
                                    padding: '10px 0 6px',
                                    borderTop: statusVariant === 'objection' ? '1px solid #F3D0D0' : '1px solid #E5E7EB',
                                    textAlign: 'left'
                                  }}
                                >
                                  <div
                                    style={{
                                      color: statusTone.heading,
                                      fontSize: '0.78rem',
                                      lineHeight: '1.2',
                                      fontWeight: 800,
                                      letterSpacing: '0.05em',
                                      textTransform: 'uppercase'
                                    }}
                                  >
                                    Status
                                  </div>
                                  <div
                                    style={{
                                      marginTop: '6px',
                                      color: statusTone.text,
                                      fontSize: '0.98rem',
                                      lineHeight: '1.35',
                                      fontWeight: 600
                                    }}
                                  >
                                    {statusLabel}
                                  </div>

                                  {statusVariant === 'partial' && (
                                    <div
                                      style={{
                                        marginTop: '10px',
                                        color: statusTone.text,
                                        fontSize: '0.95rem',
                                        lineHeight: '1.45',
                                        fontWeight: 500
                                      }}
                                    >
                                      <RedactedAuditText text={detailReason} onClick={scrollToBuyButtonSection} />
                                    </div>
                                  )}

                                  {statusVariant === 'objection' && (
                                    <>
                                      <div
                                        style={{
                                          marginTop: '14px',
                                          color: statusTone.heading,
                                          fontSize: '0.78rem',
                                          lineHeight: '1.2',
                                          fontWeight: 800,
                                          letterSpacing: '0.05em',
                                          textTransform: 'uppercase'
                                        }}
                                      >
                                        Begründung
                                      </div>
                                      <div
                                        style={{
                                          marginTop: '6px',
                                          color: statusTone.text,
                                          fontSize: '0.98rem',
                                          lineHeight: '1.45',
                                          fontWeight: 500
                                        }}
                                      >
                                        <RedactedAuditText text={detailReason} onClick={scrollToBuyButtonSection} />
                                      </div>

                                      <div
                                        style={{
                                          marginTop: '14px',
                                          color: statusTone.heading,
                                          fontSize: '0.78rem',
                                          lineHeight: '1.2',
                                          fontWeight: 800,
                                          letterSpacing: '0.05em',
                                          textTransform: 'uppercase'
                                        }}
                                      >
                                        Abzug
                                      </div>
                                      <div
                                        style={{
                                          marginTop: '6px',
                                          color: statusTone.text,
                                          fontSize: '1.02rem',
                                          lineHeight: '1.35',
                                          fontWeight: 700
                                        }}
                                      >
                                        {objectionAbzug}
                                      </div>
                                      <ObjectionEnforcementHint onClick={scrollToBuyButtonSection} />
                                    </>
                                  )}

                                  {isParent && !isParentExpanded && (
                                    <div style={{ marginTop: '12px' }}>
                                      <button
                                        type="button"
                                        onClick={toggleSubPositions}
                                        style={subPositionExpandStyle}
                                      >
                                        {childLabelExpand}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {isParent && isParentExpanded && (
                              <div style={{ marginTop: '6px', marginBottom: '6px', marginLeft: indices.length > 1 ? '50px' : '38px', textAlign: 'left' }}>
                                <button
                                  type="button"
                                  className="position-expand-link"
                                  onClick={toggleSubPositions}
                                  style={subPositionCollapseStyle}
                                >
                                  {childLabelCollapse}
                                </button>
                              </div>
                            )}
                            {isParent && isParentExpanded && (
                              <div style={{ paddingLeft: '16px' }}>
                                {renderPositionen(children, indices)}
                              </div>
                            )}
                          </div>
                        );
                      });

                    const hasAnyPositionObjection = (items) => {
                      if (!Array.isArray(items) || items.length === 0) return false;
                      return items.some((position) => {
                        if (hasWiderspruch(position?.widerspruch)) return true;
                        const children = Array.isArray(position?.unterpositionen) ? position.unterpositionen : [];
                        return hasAnyPositionObjection(children);
                      });
                    };

                    const countPositionObjections = (items) => {
                      if (!Array.isArray(items) || items.length === 0) return 0;
                      return items.reduce((sum, position) => {
                        const ownIssueCount = hasWiderspruch(position?.widerspruch) ? 1 : 0;
                        const children = Array.isArray(position?.unterpositionen) ? position.unterpositionen : [];
                        return sum + ownIssueCount + countPositionObjections(children);
                      }, 0);
                    };

                    const hasPositionObjections = hasAnyPositionObjection(positionen);
                    const handlungTitel = String(interruption?.empfehlung_an_mieter?.handlung_titel || '').trim();
                    const kurzinfoFuerEmail = String(auditAdInfo?.kurzinfo_fuer_email || '').trim();

                    return (
                      <div style={{ padding: '16px 18px', backgroundColor: 'rgb(255,255,255)' }}>
                        <div style={{ color: '#111827', fontSize: '1.18rem', lineHeight: '1.5rem', fontWeight: 800, textAlign: 'left' }}>Prüfbericht</div>
                        {auditAdInfo?.is_fully_acceptable !== true && handlungTitel && (
                          <div style={{ marginTop: '8px', color: '#4B5563', fontSize: '0.95rem', lineHeight: '1.4rem', textAlign: 'left' }}>
                            {handlungTitel}
                          </div>
                        )}
                        {auditAdInfo?.is_fully_acceptable !== true && kurzinfoFuerEmail && (
                          <div style={{ marginTop: '6px', color: '#4B5563', fontSize: '0.95rem', lineHeight: '1.4rem', textAlign: 'left' }}>
                            {kurzinfoFuerEmail}
                          </div>
                        )}
                        {auditAdInfo?.is_fully_acceptable !== true && (
                          <div style={{ marginTop: '6px', color: '#4B5563', fontSize: '0.95rem', lineHeight: '1.4rem', textAlign: 'left' }}>
                            Ihre Abrechnung ist <span style={{ fontWeight: 800, color: '#111827' }}>nicht akzeptabel</span> weil{' '}
                            <RedactedAuditText
                              text="durchgreifende Einwendungen bestehen. Für vollständige Details und ein maßgeschneidertes Widerspruchsschreiben"
                              onClick={scrollToBuyButtonSection}
                              forceRedacted
                              linkLabel="klicken sie hier."
                              linkStyle={{ '--redacted-link-color': '#000000' }}
                            />
                          </div>
                        )}
                        {auditAdInfo?.is_fully_acceptable === true && !interruption && (
                          <div style={{ marginTop: '8px', color: '#4B5563', fontSize: '0.95rem', lineHeight: '1.4rem', textAlign: 'left' }}>
                            {auditAdInfo?.has_contract === false ? (
                              <>
                                Ohne Einbeziehung des Mietvertrags können wir keine Unzulässigkeiten feststellen. Für die Prüfung ist der Mietvertrag jedoch von wesentlicher Bedeutung. Wir empfehlen Ihnen daher, Ihre Unterlagen erneut und vollständig - einschließlich des Mietvertrags - bei uns einzureichen.
                              </>
                            ) : (
                              <>
                                Ihre Abrechnung ist <span style={{ fontWeight: 800, color: '#111827' }}>vollständig akzeptabel</span>. Den
                                vollständigen Prüfbericht mit Begründung, Detailanalyse und Empfehlungen sehen Sie nach dem Kauf.{' '}
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    scrollToBuyButtonSection();
                                  }}
                                  style={{
                                    border: 'none',
                                    padding: 0,
                                    margin: 0,
                                    background: 'transparent',
                                    color: 'inherit',
                                    fontSize: 'inherit',
                                    lineHeight: 'inherit',
                                    fontFamily: 'inherit',
                                    fontWeight: 800,
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '2px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  klicken sie hier.
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        <div style={{ marginTop: '14px', textAlign: 'left' }}>
                          <div style={{ color: '#6B7280', fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            Abrechnungszeitraum
                          </div>
                          <div style={{ marginTop: '4px', color: '#111827', fontSize: '1.02rem', lineHeight: '1.4rem' }}>{periodLabel}</div>
                        </div>

                        {(() => {
                          const reasonsIssueCount = reasons.length;
                          const positionIssueCount = countPositionObjections(positionen);
                          const heizkostenIssueCount = hasHeizkosten && heizkostenIssue ? 1 : 0;
                          const reasonsAbzugSum = reasons.reduce((sum, reason) => {
                            const euroAbzug = Number(reason?.widerspruch?.euro_abzug);
                            return sum + (Number.isFinite(euroAbzug) ? euroAbzug : 0);
                          }, 0);
                          const positionAbzugSum = getPositionAbzugSum(positionen);
                          const heizkostenAbzug = Number(heizkosten?.widerspruch?.euro_abzug);
                          const heizkostenAbzugSum = Number.isFinite(heizkostenAbzug) ? heizkostenAbzug : 0;
                          const reportTabs = [
                            { key: 'summary', label: 'Zusammenfassung' },
                            reasons.length > 0 ? { key: 'reasons', label: 'Grundsätzliche Mängel' } : null,
                            positionen.length > 0 ? { key: 'positionen', label: 'Einzel-Kosten' } : null,
                            hasHeizkosten ? { key: 'heizkosten', label: 'Heizkosten' } : null
                          ].filter(Boolean);
                          const activeReportTab = reportTabs.some((tab) => tab.key === auditReportTab)
                            ? auditReportTab
                            : reportTabs[0]?.key;

                          if (reportTabs.length === 0) return null;

                          return (
                            <div style={{ marginTop: '22px', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', borderBottom: '1px solid #E5E7EB', paddingBottom: '8px' }}>
                                {reportTabs.map((tab) => {
                                  const isActive = tab.key === activeReportTab;
                                  return (
                                    <button
                                      key={tab.key}
                                      type="button"
                                      onClick={() => setAuditReportTab(tab.key)}
                                      style={{
                                        border: 'none',
                                        borderRadius: '9999px',
                                        cursor: 'pointer',
                                        padding: '6px 12px',
                                        fontSize: '0.8rem',
                                        fontWeight: 700,
                                        background: isActive ? '#111827' : '#F3F4F6',
                                        color: isActive ? '#FFFFFF' : '#374151'
                                      }}
                                    >
                                      {tab.label}
                                    </button>
                                  );
                                })}
                              </div>

                              {activeReportTab === 'summary' && (
                                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  {[
                                    reasons.length > 0
                                      ? {
                                          key: 'reasons',
                                          label: 'Grundsätzliche Mängel',
                                          count: reasonsIssueCount,
                                          abzug: reasonsAbzugSum,
                                          available: true,
                                          missingSubmission: false
                                        }
                                      : null,
                                    {
                                      key: 'positionen',
                                      label: 'Einzel-Kosten',
                                      count: positionIssueCount,
                                      abzug: positionAbzugSum,
                                      available: positionen.length > 0,
                                      missingSubmission: false
                                    },
                                    {
                                      key: 'heizkosten',
                                      label: 'Heizkosten',
                                      count: heizkostenIssueCount,
                                      abzug: heizkostenAbzugSum,
                                      available: hasHeizkosten,
                                      missingSubmission: isHeizkostenMissing
                                    }
                                  ].filter(Boolean).map((item) => (
                                    <div
                                      key={`summary-${item.key}`}
                                      onClick={() => item.available && setAuditReportTab(item.key)}
                                      onMouseEnter={() => item.available && setHoveredSummaryKey(item.key)}
                                      onMouseLeave={() => item.available && setHoveredSummaryKey(null)}
                                      onKeyDown={(event) => {
                                        if (!item.available) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          setAuditReportTab(item.key);
                                        }
                                      }}
                                      role={item.available ? 'button' : undefined}
                                      tabIndex={item.available ? 0 : undefined}
                                      style={{
                                        borderRadius: '10px',
                                        border: item.available && hoveredSummaryKey === item.key ? '1px solid #D1D5DB' : '1px solid #E5E7EB',
                                        background: item.available && hoveredSummaryKey === item.key ? '#F3F4F6' : '#F9FAFB',
                                        padding: '12px',
                                        cursor: item.available ? 'pointer' : 'default'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                        <div>
                                          <div style={{ color: '#111827', fontSize: '0.92rem', fontWeight: 800 }}>
                                            {item.label}
                                          </div>
                                          <div
                                            style={{
                                              marginTop: '6px',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '5px',
                                              padding: '5px 10px',
                                              borderRadius: '9999px',
                                              background: item.missingSubmission ? '#F3F4F6' : item.count > 0 ? '#FEE2E2' : '#DCFCE7',
                                              color: item.missingSubmission ? '#4B5563' : item.count > 0 ? '#B91C1C' : '#166534',
                                              fontSize: '0.78rem',
                                              fontWeight: 800
                                            }}
                                          >
                                            {item.available
                                              ? item.missingSubmission
                                                ? 'Nicht eingereicht'
                                                : item.key === 'reasons'
                                                  ? `${item.count === 1 ? '1 Problem gefunden, das die Ablehnung der Abrechnung begründet.' : `${item.count} Probleme gefunden, die die Ablehnung der Abrechnung begründen.`}`
                                                  : item.count > 0
                                                    ? `Kürzung um ${formatEuro(item.abzug)} gerechtfertigt durch ${item.count} ${item.count === 1 ? 'gefundenen' : 'gefundene'} Fehler.`
                                                    : 'Alles in Ordnung'
                                              : 'Keine Details verfügbar'}
                                          </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                          <div
                                            style={{
                                              borderRadius: '9999px',
                                              padding: '6px 12px',
                                              fontSize: '0.78rem',
                                              fontWeight: 700,
                                              background: item.available ? '#E5E7EB' : '#F3F4F6',
                                              color: item.available ? '#111827' : '#9CA3AF'
                                            }}
                                          >
                                            {item.available ? 'Jetzt ansehen' : 'Keine Details'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {activeReportTab === 'reasons' && (
                                <div style={{ marginTop: '14px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {reasons.map((reason, idx) => (
                                      <div
                                        key={`reason-card-${idx}`}
                                        style={{
                                          borderRadius: '8px',
                                          border: '1px solid #F3D0D0',
                                          background: '#FEF3F3',
                                          padding: '12px 14px'
                                        }}
                                      >
                                        <div style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '6px', background: '#FADDDD', color: '#B94141', fontSize: '0.74rem', fontWeight: 700 }}>
                                          Grundsätzlicher Mangel festgestellt
                                        </div>
                                        <div style={{ marginTop: '10px', color: '#C2410C', fontSize: '0.79rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                          Begründung
                                        </div>
                                        {getReasonTitle(reason) && (
                                          <>
                                            <div style={{ marginTop: '10px', color: '#C2410C', fontSize: '0.79rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                              Titel
                                            </div>
                                            <div style={{ marginTop: '4px', color: '#7F1D1D', fontSize: '1rem', lineHeight: '1.4rem', fontWeight: 700 }}>
                                              <RedactedAuditText text={getReasonTitle(reason)} onClick={scrollToBuyButtonSection} />
                                            </div>
                                          </>
                                        )}
                                        <div style={{ marginTop: '4px', color: '#7F1D1D', fontSize: '1rem', lineHeight: '1.4rem' }}>
                                          <RedactedAuditText text={getReasonBody(reason)} onClick={scrollToBuyButtonSection} />
                                        </div>
                                        <div style={{ marginTop: '10px', color: '#C2410C', fontSize: '0.79rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                          Abzug
                                        </div>
                                        <div style={{ marginTop: '4px', color: '#B91C1C', fontSize: '1.05rem', lineHeight: '1.4rem', fontWeight: 800 }}>
                                          <RedactedAuditText text={getReasonAbzug(reason)} onClick={scrollToBuyButtonSection} />
                                        </div>
                                        <ObjectionEnforcementHint onClick={scrollToBuyButtonSection} />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {activeReportTab === 'positionen' && (
                                <div style={{ marginTop: '14px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                    <div style={{ color: '#6B7280', fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                      Einzel-Kosten
                                    </div>
                                    {!hasPositionObjections && (
                                      <div
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          padding: '4px 10px',
                                          borderRadius: '9999px',
                                          background: '#DCFCE7',
                                          color: '#166534',
                                          fontSize: '0.74rem',
                                          fontWeight: 700
                                        }}
                                      >
                                        Akzeptiert
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ marginTop: '4px', color: '#4B5563', fontSize: '0.9rem', lineHeight: '1.3rem' }}>
                                    Für Details bitte auf eine Position <span style={{ fontWeight: 800, color: '#111827', textDecoration: 'underline' }}>klicken</span>.
                                  </div>
                                  <div style={{ marginTop: '8px' }}>
                                    {renderPositionen(positionen)}
                                  </div>
                                </div>
                              )}

                              {activeReportTab === 'heizkosten' && (
                                <div style={{ marginTop: '14px' }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      marginBottom: '8px',
                                      flexWrap: 'wrap'
                                    }}
                                  >
                                    <div style={{ color: '#6B7280', fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                      Heizkosten
                                    </div>
                                    {isHeizkostenMissing && (
                                      <div
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          padding: '4px 10px',
                                          borderRadius: '9999px',
                                          background: '#F3F4F6',
                                          color: '#4B5563',
                                          fontSize: '0.74rem',
                                          fontWeight: 700
                                        }}
                                      >
                                        Nicht eingereicht
                                      </div>
                                    )}
                                    {isHeizkostenAccepted && (
                                      <div
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          padding: '4px 10px',
                                          borderRadius: '9999px',
                                          background: '#DCFCE7',
                                          color: '#166534',
                                          fontSize: '0.74rem',
                                          fontWeight: 700
                                        }}
                                      >
                                        Akzeptiert
                                      </div>
                                    )}
                                  </div>
                                  {isHeizkostenMissing ? (
                                    <div style={{ color: '#374151', fontSize: '1rem', lineHeight: '1.4rem' }}>
                                      Heizkostenabrechnung wurde nicht eingereicht.
                                    </div>
                                  ) : isHeizkostenAccepted ? (
                                    <div style={{ color: '#374151', fontSize: '0.9rem', lineHeight: '1.3rem' }}>
                                      Heizkostenabrechnung eingereicht, geprüft und akzeptabel.
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        borderRadius: '8px',
                                        border: `1px solid ${heizkostenIssue ? '#F3D0D0' : '#BBF7D0'}`,
                                        background: heizkostenIssue ? '#FEF3F3' : '#F0FDF4',
                                        padding: '12px 14px'
                                      }}
                                    >
                                      <>
                                        <div
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '4px 10px',
                                            borderRadius: '9999px',
                                            background: heizkostenIssue ? '#FADDDD' : '#DCFCE7',
                                            color: heizkostenIssue ? '#B94141' : '#166534',
                                            fontSize: '0.74rem',
                                            fontWeight: 700
                                          }}
                                        >
                                          {heizkostenIssue ? 'Heizkostenabrechnung beanstandet' : 'Heizkostenabrechnung unauffällig'}
                                        </div>
                                        <div style={{ marginTop: '10px', color: '#C2410C', fontSize: '0.79rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                          Begründung
                                        </div>
                                        <div style={{ marginTop: '4px', color: '#7F1D1D', fontSize: '1rem', lineHeight: '1.4rem' }}>
                                          <RedactedAuditText
                                            text={
                                              getFirstSentenceOrLine(heizkosten?.widerspruch?.begruendung) ||
                                              getFirstSentenceOrLine(heizkosten?.begruendung) ||
                                              'Details zur Heizkostenprüfung werden im vollständigen Bericht angezeigt.'
                                            }
                                            onClick={scrollToBuyButtonSection}
                                          />
                                        </div>
                                        <div style={{ marginTop: '10px', color: '#C2410C', fontSize: '0.79rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                          Abzug
                                        </div>
                                        <div style={{ marginTop: '4px', color: '#B91C1C', fontSize: '1.05rem', lineHeight: '1.4rem', fontWeight: 800 }}>
                                          {(() => {
                                            const euroAbzug = Number(heizkosten?.widerspruch?.euro_abzug);
                                            if (Number.isFinite(euroAbzug)) return formatEuro(euroAbzug);
                                            return getFirstSentenceOrLine(heizkosten?.widerspruch?.abzug_text) || '—';
                                          })()}
                                        </div>
                                        <ObjectionEnforcementHint onClick={scrollToBuyButtonSection} />
                                      </>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {!hasSubmittedPreSaleQuestion && (
                          <div
                            style={{
                              marginTop: '24px',
                              border: '1px solid #BBF7D0',
                              borderRadius: '10px',
                              background: '#F0FDF4',
                              padding: '12px'
                            }}
                          >
                            <div style={{ color: '#14532D', fontSize: '1rem', lineHeight: '1.45rem', fontWeight: 800 }}>
                              Haben Sie eine Frage?
                            </div>
                            <div style={{ marginTop: '4px', color: '#5B21B6', fontSize: '0.9rem', lineHeight: '1.4rem', fontWeight: 500 }}>
                              Ihr Prüf-Betreuer hilft Ihnen gerne weiter, bevor Sie Ihren Prüfbericht und Widerspruch kaufen.
                            </div>

                            <textarea
                              value={preSaleQuestion}
                              onChange={(event) => {
                                setPreSaleQuestion(event.target.value);
                                if (preSaleQuestionError) setPreSaleQuestionError(null);
                              }}
                              placeholder="Stellen Sie hier Ihre Frage..."
                              maxLength={4000}
                              rows={3}
                              style={{
                                marginTop: '10px',
                                width: '100%',
                                resize: 'vertical',
                                border: '1px solid #BBF7D0',
                                borderRadius: '8px',
                                padding: '10px',
                                fontSize: '0.92rem',
                                lineHeight: '1.4rem',
                                fontFamily: 'inherit',
                                fontWeight: 500,
                                color: '#111827',
                                background: '#FFFFFF',
                                boxSizing: 'border-box'
                              }}
                            />

                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  border: '1px solid #DDD6FE',
                                  borderRadius: '9999px',
                                  background: '#FFFFFF',
                                  padding: '6px 10px',
                                  color: '#5B21B6',
                                  fontSize: '0.82rem',
                                  lineHeight: 1.2,
                                  fontWeight: 700
                                }}
                              >
                                <span
                                  style={{
                                    position: 'relative',
                                    width: '26px',
                                    height: '26px',
                                    flex: '0 0 26px',
                                    overflow: 'visible',
                                    display: 'inline-flex'
                                  }}
                                >
                                  <span
                                    style={{
                                      width: '26px',
                                      height: '26px',
                                      borderRadius: '9999px',
                                      overflow: 'hidden',
                                      display: 'inline-flex'
                                    }}
                                  >
                                    <img
                                      src={LennartRugenProfile}
                                      alt="Lennart Rugen"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  </span>
                                  <span
                                    style={{
                                      position: 'absolute',
                                      right: '-1px',
                                      bottom: '-1px',
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '9999px',
                                      background: '#22C55E',
                                      border: '1.5px solid #FFFFFF',
                                      zIndex: 2
                                    }}
                                  />
                                </span>
                                Sie betreut: Lennart (durchschnittl. Antwortzeit 53 Minuten)
                              </div>
                              <button
                                type="button"
                                onClick={submitPreSaleQuestion}
                                disabled={preSaleQuestionSending || preSaleQuestion.trim().length === 0}
                                style={{
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '10px 14px',
                                  fontSize: '0.88rem',
                                  lineHeight: 1,
                                  fontWeight: 800,
                                  cursor:
                                    preSaleQuestionSending || preSaleQuestion.trim().length === 0
                                      ? 'not-allowed'
                                      : 'pointer',
                                  color: '#FFFFFF',
                                  background:
                                    preSaleQuestionSending || preSaleQuestion.trim().length === 0
                                      ? '#9CA3AF'
                                      : '#16a34a'
                                }}
                              >
                                {preSaleQuestionSending ? 'Wird gesendet...' : 'Senden'}
                              </button>
                            </div>

                            {preSaleQuestionError && (
                              <div style={{ marginTop: '8px', color: '#B91C1C', fontSize: '0.86rem', lineHeight: '1.35rem' }}>
                                {String(preSaleQuestionError)}
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    );
                  })()}

                  <div
                    ref={wasJetztSectionRef}
                    style={{
                      marginTop: auditAdInfo?.is_fully_acceptable === true && !auditAdInfo?.interruption ? '0' : '40px',
                      padding: auditAdInfo?.is_fully_acceptable === true && !auditAdInfo?.interruption ? '0 18px 12px' : '18px 18px 12px',
                      borderTop: auditAdInfo?.is_fully_acceptable === true && !auditAdInfo?.interruption ? 'none' : '1px solid rgb(229,229,229)',
                      backgroundColor: 'rgb(255,255,255)'
                    }}
                  >
                    <div
                      style={{
                        background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)',
                        padding: '14px 14px 16px'
                      }}
                    >
                      <div style={{ color: '#111827', fontSize: '1.15rem', lineHeight: '1.45rem', fontWeight: 900 }}>
                        Was jetzt?
                      </div>
                      <div style={{ marginTop: '10px', color: '#374151', fontSize: '0.97rem', lineHeight: '1.5rem' }}>
                        {auditAdInfo?.is_fully_acceptable === true && !auditAdInfo?.interruption ? (
                          <>
                            {auditAdInfo?.has_contract === false ? (
                              <>
                                Ohne Einbeziehung des Mietvertrags können wir keine Unzulässigkeiten feststellen. Für die Prüfung ist der Mietvertrag jedoch von wesentlicher Bedeutung. Wir empfehlen Ihnen daher, Ihre Unterlagen erneut und vollständig - einschließlich des Mietvertrags - bei uns einzureichen.
                              </>
                            ) : (
                              <>
                                Ihre Abrechnung ist nach unserer Prüfung vollständig akzeptabel. Den{' '}
                                <span style={{ fontWeight: 900, color: '#111827' }}>vollständigen Prüfbericht</span> mit allen Details
                                können Sie unten kaufen.
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            Damit Ihr Widerspruch gilt, brauchen Sie ein{' '}
                            <span style={{ fontWeight: 900, color: '#111827' }}>rechtssicheres Widerspruchsschreiben</span>. Dieses bieten wir
                            Ihnen zusammen mit einem <span style={{ fontWeight: 900, color: '#111827' }}>detaillierten Prüfbericht</span> zum
                            Kauf an.
                          </>
                        )}
                      </div>

                      {!(auditAdInfo?.is_fully_acceptable === true && !auditAdInfo?.interruption) && (
                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {[
                            'Rechtssicheren Widerspruch schreiben',
                            recommendationAction,
                            'Widerspruch an Vermieter schicken'
                          ].map((stepLabel, stepIdx) => (
                            <div
                              key={`next-step-${stepIdx}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: '10px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '10px',
                                background: '#FFFFFF',
                                padding: '10px 12px',
                                textAlign: 'left'
                              }}
                            >
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  flex: '0 0 24px',
                                  borderRadius: '9999px',
                                  background: '#16a34a',
                                  color: '#FFFFFF',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.78rem',
                                  lineHeight: 1,
                                  fontWeight: 900
                                }}
                              >
                                {stepIdx + 1}
                              </div>
                              <div style={{ flex: 1, color: '#111827', fontSize: '0.96rem', lineHeight: '1.4rem', fontWeight: 700, textAlign: 'left' }}>
                                {stepLabel}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasSubmittedPreSaleQuestion && questionnaireVisible && (
                        <div style={{ marginTop: '14px', textAlign: 'left' }}>
                          <div
                            style={{
                              border: '1px solid #BBF7D0',
                              borderRadius: '10px',
                              padding: '12px',
                              background: '#F0FDF4'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                                borderBottom: '1px solid #E9E2FF',
                                paddingBottom: '12px',
                                marginBottom: '12px',
                                textAlign: 'center'
                              }}
                            >
                              <div
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  flex: '0 0 28px',
                                  borderRadius: '9999px',
                                  background: '#22C55E',
                                  color: '#FFFFFF',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.92rem',
                                  lineHeight: 1,
                                  fontWeight: 900
                                }}
                              >
                                ✓
                              </div>
                              <div style={{ color: '#14532D', fontSize: '1rem', lineHeight: '1.35rem', fontWeight: 800 }}>
                                Frage erhalten
                              </div>
                              <div style={{ color: '#5B21B6', fontSize: '0.9rem', lineHeight: '1.45rem', fontWeight: 500, maxWidth: '520px' }}>
                                Vielen Dank. Wir antworten Ihnen <span style={{ fontWeight: 800 }}>so schnell wie möglich</span> per{' '}
                                <span style={{ fontWeight: 800 }}>E-Mail</span>. Wenn Sie uns etwas zurückgeben möchten, freuen wir uns über
                                Ihre <span style={{ fontWeight: 800 }}>Teilnahme am Fragebogen</span> unten.
                              </div>
                            </div>
                            {questionnaireLoading && (
                              <div style={{ color: '#6B7280', fontSize: '0.9rem' }}>Fragebogen wird geladen...</div>
                            )}

                            {questionnaireError && (
                              <div style={{ color: '#B91C1C', fontSize: '0.9rem' }}>
                                {String(questionnaireError)}
                              </div>
                            )}

                            {!questionnaireLoading && !questionnaireError && questionnaireQuestions.length > 0 && (() => {
                              const currentQuestion = questionnaireQuestions[questionnaireStepIndex];
                              if (!currentQuestion) return null;
                              const answerOptions = Array.isArray(currentQuestion?.answer_options) ? currentQuestion.answer_options : [];
                              const selectedOptions = questionnaireAnswers[questionnaireStepIndex] || [];
                              const allowMultiSelect = currentQuestion?.allow_multi_select === true;
                              const hasNext = questionnaireStepIndex < questionnaireQuestions.length - 1;
                              const hasSelection = selectedOptions.length > 0;
                              const buttonLabel = hasNext ? 'Weiter' : 'Fertig';

                              if (questionnaireCompleted) {
                                return (
                                  <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#6B7280' }}>
                                    Danke für Ihre Teilnahme
                                  </div>
                                );
                              }

                              return (
                                <div>
                                  <div style={{ fontSize: '0.98rem', fontWeight: 700, color: '#111827' }}>
                                    {currentQuestion?.question || `Frage ${questionnaireStepIndex + 1}`}
                                  </div>
                                  {allowMultiSelect && (
                                    <div style={{ marginTop: '4px', fontSize: '0.78rem', color: '#6B7280' }}>
                                      Mehrfachauswahl möglich
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                                    {answerOptions.map((option, optionIndex) => {
                                      const isSelected = selectedOptions.includes(optionIndex);
                                      return (
                                        <div
                                          key={`question-${questionnaireStepIndex}-option-${optionIndex}`}
                                          style={{
                                            border: `1px solid ${isSelected ? '#BBF7D0' : '#E5E7EB'}`,
                                            borderRadius: '8px',
                                            padding: '8px 10px',
                                            background: isSelected ? '#EFE9FF' : '#FFFFFF'
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: 'flex',
                                              alignItems: 'flex-start',
                                              gap: '8px',
                                              cursor: 'pointer',
                                              color: '#111827',
                                              fontSize: '0.95rem',
                                              lineHeight: '1.4rem'
                                            }}
                                          >
                                            <input
                                              type={allowMultiSelect ? 'checkbox' : 'radio'}
                                              name={`question-${questionnaireStepIndex}`}
                                              checked={isSelected}
                                              onChange={() =>
                                                handleQuestionnaireAnswerChange(questionnaireStepIndex, optionIndex, allowMultiSelect)
                                              }
                                              style={{ marginTop: '3px' }}
                                            />
                                            <span>{option}</span>
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {allowMultiSelect && hasSelection && (
                                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (hasNext) {
                                            setQuestionnaireStepIndex((prev) => Math.min(prev + 1, questionnaireQuestions.length - 1));
                                          } else {
                                            setQuestionnaireCompleted(true);
                                          }
                                        }}
                                        style={{
                                          background: '#111827',
                                          color: 'white',
                                          border: 'none',
                                          padding: '10px 16px',
                                          borderRadius: '8px',
                                          cursor: 'pointer',
                                          fontSize: '0.9rem',
                                          fontWeight: 700
                                        }}
                                      >
                                        {buttonLabel}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {questionnaireSubmitError && (
                              <div style={{ marginTop: '8px', color: '#B91C1C', fontSize: '0.9rem' }}>
                                {String(questionnaireSubmitError)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CTA ROW */}
                  <div ref={buyButtonSectionRef} style={{ padding: '16px 18px', borderTop: '1px solid rgb(229,229,229)', backgroundColor: 'rgb(250,250,250)', textAlign: 'center' }}>
                    <button
                      onClick={() => {
                        const paymentLink = progressData?.payment_link_url;
                        if (paymentLink) {
                          window.location.assign(paymentLink);
                          return;
                        }
                        alert('Der Zahlungslink ist noch nicht verfügbar. Bitte laden Sie die Seite später erneut.');
                      }}
                      style={{
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        width: '100%',
                        maxWidth: '420px',
                        background: 'linear-gradient(135deg,#16a34a 0%, #15803d 55%, #14532d 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '16px 24px',
                        borderRadius: '14px',
                        fontSize: '18px',
                        fontWeight: 900,
                        cursor: 'pointer',
                        boxShadow: '0 8px 22px 0 rgba(57, 35, 150, 0.35)'
                      }}
                    >
                      {(() => {
                        const priceValue = Number(auditAdInfo?.price);
                        const priceLabel = Number.isFinite(priceValue) ? formatEuro(priceValue) : null;
                        const subtitle = priceLabel ? `Jetzt kaufen für ${priceLabel}` : 'Jetzt kaufen';
                        return (
                          <>
                            <div style={{ fontSize: '18px', lineHeight: '1.35rem', fontWeight: 900 }}>Widerspruch &amp; Prüfbericht</div>
                            <div style={{ marginTop: '4px', fontSize: '12px', lineHeight: '1.1rem', fontWeight: 800, opacity: 0.9 }}>
                              {subtitle}
                            </div>
                          </>
                        );
                      })()}
                    </button>
                    <div
                      style={{
                        margin: '16px auto 0',
                        width: '100%',
                        maxWidth: '420px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#9CA3AF',
                        gap: '10px'
                      }}
                    >
                      <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.02em' }}>Oder</span>
                      <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('click');
                        trackSubscriptionPricingOptionClick('pricing_membership_fake_button', {
                          option: 'mieterschutz_membership',
                          plan: 'monthly',
                          monthly_price_eur: 5,
                          is_fake_button: true
                        });
                        window.alert('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
                      }}
                      style={{
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        marginTop: '14px',
                        width: '100%',
                        maxWidth: '420px',
                        background: '#F5F3FF',
                        color: '#14532D',
                        border: '1px solid #DDD6FE',
                        padding: '16px 20px 14px',
                        borderRadius: '14px',
                        fontSize: '16px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px 0 rgba(76,29,149,0.10)',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '-12px',
                          right: '-10px',
                          background: '#15803d',
                          color: '#FFFFFF',
                          border: '2px solid #DCFCE7',
                          borderRadius: '10px',
                          padding: '6px 10px',
                          fontSize: '11px',
                          lineHeight: 1,
                          fontWeight: 900,
                          letterSpacing: '0.01em',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 8px 16px rgba(76,29,149,0.25)',
                          transform: 'rotate(6deg)'
                        }}
                      >
                        5 € im Monat
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px'
                        }}
                      >
                        <div style={{ fontSize: '16px', lineHeight: '1.35rem', fontWeight: 900, textAlign: 'center' }}>
                          Gratis für Mitglieder im Mieterschutz
                        </div>
                      </div>
                      <div
                        style={{
                          marginTop: '4px',
                          fontSize: '12px',
                          lineHeight: '1.2rem',
                          fontWeight: 700,
                          color: '#6D28D9',
                          textAlign: 'center'
                        }}
                      >
                        Deutschlands günstigster Schutz für Mieter
                      </div>
                    </button>
                    <div style={{ marginTop: '10px', fontSize: '0.95rem', lineHeight: '1.4rem', color: 'rgb(75,85,99)' }}>
                      Freischalten in weniger als 1 Minute – danach sehen Sie die genaue Anleitung, Details und vollständige Erklärungen.
                    </div>
                    {auditAdInfo?.has_contract === false && (
                      <div
                        style={{
                          marginTop: '12px',
                          color: '#6B7280',
                          background: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          padding: '10px 14px',
                          borderRadius: '10px',
                          fontSize: '0.95rem',
                          lineHeight: '1.5',
                          textAlign: 'left'
                        }}
                      >
                        <strong>Hinweis:</strong> Sie haben keinen Mietvertrag eingereicht. Der Mietvertrag beeinflusst das
                        Prüfergebnis und die Beurteilung kann sich ändern. Nach dem Kauf können Sie den Mietvertrag kostenfrei nachreichen.
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '28px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    width: '100%'
                  }}
                >
                  <img
                    src={KoerberStiftungLogo}
                    alt="Körber Stiftung"
                    style={{ height: '38px', width: 'auto', objectFit: 'contain' }}
                  />
                  <img
                    src={DsgvoLogo}
                    alt="DSGVO"
                    style={{ height: '38px', width: 'auto', objectFit: 'contain' }}
                  />
                  <div
                    style={{
                      fontSize: '0.8rem',
                      lineHeight: '1.15rem',
                      color: '#000000',
                      textAlign: 'center',
                      fontWeight: 700,
                      whiteSpace: 'pre-line'
                    }}
                  >
                    Eingetragener{'\n'}Rechtsdienstleister{'\n'}
                    <span style={{ fontSize: '0.7rem' }}>VII 5-2025 0001 1629</span>
                  </div>
                </div>
                  <div style={{ 
                    marginTop: '16px', 
                    fontSize: '0.875rem', 
                    color: '#6B7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <span>🔒</span>
                    <span>Sicherer Checkout • SSL-verschlüsselt</span>
                  </div>
                  
                  {/* Supported payment methods display */}
                  {supportedPaymentLogos.length > 0 && (
                    <div style={{ 
                      marginTop: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Unterstützte Zahlungsmethoden:</span>
                      {supportedPaymentLogos.map((logo, index) => (
                        <img 
                          key={index}
                          src={logo} 
                          alt="Payment method" 
                          style={{ height: '24px', width: 'auto' }}
                        />
                      ))}
                    </div>
                  )}
              </div>
            )}

            {/* Handle COMPLETED state */}
            {progressData.state === ProgressState.COMPLETED && (
              <div>
                <div style={{ 
                  background: '#ECFDF5',
                  border: '1px solid #A7F3D0',
                  padding: '24px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      role="img"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="#059669"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    color: '#065F46',
                    fontSize: '1.25rem',
                    fontWeight: '600'
                  }}>
                    Prüfung abgeschlossen und Zahlung erhalten
                  </h3>
                  <div style={{
                    color: '#065F46',
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    marginBottom: '12px'
                  }}>
                    Du wirst gleich automatisch weitergeleitet.
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => {
                        const link = progressData?.submission_access_link;
                        if (intervalRef.current) clearInterval(intervalRef.current);
                        if (link) {
                          window.location.assign(link);
                        } else {
                          window.alert('Der Link ist noch nicht verfügbar. Bitte warten Sie einen Moment und versuchen Sie es erneut.');
                        }
                      }}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                      onMouseOver={(e) => e.target.style.background = '#059669'}
                      onMouseOut={(e) => e.target.style.background = '#10B981'}
                    >
                      Falls die Weiterleitung nicht funktioniert, klicke hier
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Handle LOITERING state */}
            {progressData.state === ProgressState.LOITERING && (
              <div>
                <div style={{ 
                  background: '#ECFDF5',
                  border: '1px solid #A7F3D0',
                  padding: '24px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      role="img"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="#059669"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 style={{
                    margin: '0 0 16px 0',
                    color: '#065F46',
                    fontSize: '1.25rem',
                    fontWeight: '600'
                  }}>
                    Zahlung erfolgreich erhalten
                  </h3>
                  <div style={{
                    color: '#065F46',
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    marginBottom: '16px'
                  }}>
                    Ihre Zahlung wurde erfolgreich erhalten und wird nun validiert. Dieser Prozess dauert in der Regel weniger als 4 Stunden an Werktagen.
                  </div>
                  <div style={{
                    color: '#047857',
                    fontSize: '0.95rem',
                    lineHeight: '1.5',
                    marginBottom: '20px',
                    background: '#F0FDF4',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #BBF7D0'
                  }}>
                    Sie erhalten eine E-Mail, sobald die Validierung abgeschlossen ist.
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent('Frage zur Zahlungsvalidierung - Support-Anfrage');
                        const body = encodeURIComponent(
                          `Sehr geehrtes Nebenkostenprofi Team,\n\n` +
                          `ich habe eine Frage zur Validierung meiner Zahlung.\n\n` +
                          `Prüfung: ${auditId}\n` +
                          `Schlüssel: ${accessKey || 'Nicht angegeben'}\n\n` +
                          `Vielen Dank!`
                        );
                        window.location.href = `mailto:support@nebenkostenprofi.de?subject=${subject}&body=${body}`;
                      }}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                      onMouseOver={(e) => e.target.style.background = '#059669'}
                      onMouseOut={(e) => e.target.style.background = '#10B981'}
                    >
                      Support kontaktieren
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show error message for non-FAILED states */}
            {progressData.state !== ProgressState.FAILED && progressData.state !== ProgressState.AWAITING_PAYMENT && progressData.state !== ProgressState.AWAITING_SIGNOFF && progressData.error && (
              <div style={{ color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '10px 12px', borderRadius: '6px' }}>
                <strong>Meldung:</strong> {progressData.error}
              </div>
            )}
            
            {/* When submission_access_link exists, we redirect immediately via useEffect above */}
          </div>
        )}
      </div>
      {popupQuestionnaireVisible && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.55)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '560px',
              background: 'linear-gradient(160deg, #ffffff 0%, #f6f3ff 100%)',
              borderRadius: '16px',
              border: '1px solid #DDD6FE',
              boxShadow: '0 24px 48px rgba(17, 24, 39, 0.25)',
              padding: '0',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                padding: '14px 16px'
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: '#FFFFFF', fontWeight: 900 }}>
                <span
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '9999px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.45)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    lineHeight: 1
                  }}
                >
                  ?
                </span>
                <span style={{ fontSize: '1.06rem', lineHeight: '1.3rem' }}>Mini-Umfrage</span>
              </div>
              <button
                type="button"
                onClick={() => setPopupQuestionnaireVisible(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#FFFFFF',
                  fontSize: '22px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: '2px 4px',
                  fontWeight: 600
                }}
                aria-label="Popup schließen"
              >
                ×
              </button>
            </div>

            <div style={{ padding: '16px' }}>

            {popupQuestionnaireLoading && (
              <div style={{ marginTop: '12px', color: '#6B7280', fontSize: '0.9rem' }}>Fragebogen wird geladen...</div>
            )}

            {!popupQuestionnaireLoading && !popupQuestionnaireError && popupQuestionnaireQuestions.length > 0 && (() => {
              const currentQuestion = popupQuestionnaireQuestions[popupQuestionnaireStepIndex];
              if (!currentQuestion) return null;
              const answerOptions = Array.isArray(currentQuestion?.answer_options) ? currentQuestion.answer_options : [];
              const selectedOptions = popupQuestionnaireAnswers[popupQuestionnaireStepIndex] || [];
              const allowMultiSelect = currentQuestion?.allow_multi_select === true;
              const hasNext = popupQuestionnaireStepIndex < popupQuestionnaireQuestions.length - 1;
              const hasSelection = selectedOptions.length > 0;
              const buttonLabel = hasNext ? 'Weiter' : 'Fertig';

              if (popupQuestionnaireCompleted) {
                return (
                  <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '0.92rem', color: '#4B5563' }}>
                    Danke für Ihre Teilnahme.
                  </div>
                );
              }

              return (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: '#111827', lineHeight: '1.5rem' }}>
                    {currentQuestion?.question || `Frage ${popupQuestionnaireStepIndex + 1}`}
                  </div>
                  {allowMultiSelect && (
                    <div style={{ marginTop: '4px', fontSize: '0.78rem', color: '#6B7280' }}>
                      Mehrfachauswahl möglich
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px', textAlign: 'left' }}>
                    {answerOptions.map((option, optionIndex) => {
                      const isSelected = selectedOptions.includes(optionIndex);
                      return (
                        <div
                          key={`popup-question-${popupQuestionnaireStepIndex}-option-${optionIndex}`}
                          style={{
                            border: `2px solid ${isSelected ? '#16A34A' : '#E5E7EB'}`,
                            borderRadius: '10px',
                            padding: '10px 12px',
                            background: isSelected ? '#F5F3FF' : '#FFFFFF',
                            cursor: 'pointer',
                            boxShadow: isSelected ? '0 6px 14px rgba(139, 92, 246, 0.18)' : '0 2px 6px rgba(17, 24, 39, 0.08)',
                            transition: 'all 0.18s ease'
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              cursor: 'pointer',
                              color: '#111827',
                              fontSize: '0.95rem',
                              lineHeight: '1.4rem',
                              fontWeight: 600
                            }}
                          >
                            <input
                              type={allowMultiSelect ? 'checkbox' : 'radio'}
                              name={`popup-question-${popupQuestionnaireStepIndex}`}
                              checked={isSelected}
                              onChange={() =>
                                handlePopupQuestionnaireAnswerChange(popupQuestionnaireStepIndex, optionIndex, allowMultiSelect)
                              }
                              style={{ marginTop: '3px', accentColor: '#16a34a', width: '16px', height: '16px' }}
                            />
                            <span>{option}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  {allowMultiSelect && hasSelection && (
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (hasNext) {
                            setPopupQuestionnaireStepIndex((prev) => Math.min(prev + 1, popupQuestionnaireQuestions.length - 1));
                          } else {
                            setPopupQuestionnaireCompleted(true);
                          }
                        }}
                        style={{
                          background: '#111827',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 700
                        }}
                      >
                        {buttonLabel}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {!popupQuestionnaireLoading && !popupQuestionnaireError && popupQuestionnaireQuestions.length === 0 && (
              <div style={{ marginTop: '12px', color: '#6B7280', fontSize: '0.9rem', lineHeight: '1.4rem' }}>
                Der Fragebogen ist derzeit leer.
              </div>
            )}

            {popupQuestionnaireSubmitError && (
              <div style={{ marginTop: '10px', color: '#B91C1C', fontSize: '0.88rem' }}>
                {String(popupQuestionnaireSubmitError)}
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
    <div className="status-footer" style={{ width: '100%', background: '#F3F4F6', borderTop: '1px solid #E5E7EB', padding: '16px 24px', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div className="status-footer-content" style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', boxSizing: 'border-box' }}>
        <div className="status-footer-links" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minWidth: 0, flex: '1 1 auto' }}>
          <a href={AGB_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>AGB</a>
          <span style={{ color: '#9CA3AF' }}>|</span>
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Datenschutzbestimmungen</a>
          <span style={{ color: '#9CA3AF' }}>|</span>
          <a href={'https://www.nebenkostenprofi.de'} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Startseite</a>
        </div>
        <button
          onClick={() => {
            const subject = encodeURIComponent('Support-Anfrage');
            const body = encodeURIComponent(
              `Sehr geehrtes Nebenkostenprofi Team,\n\n` +
              `ich benötige Unterstützung.\n\n` +
              `Prüfung: ${auditId}\n` +
              `Schlüssel: ${accessKey || 'Nicht angegeben'}\n\n` +
              `Vielen Dank!`
            );
            window.location.href = `mailto:info@nebenkostenprofi.de?subject=${subject}&body=${body}`;
          }}
          className="status-footer-button"
          style={{
            background: '#111827',
            color: 'white',
            border: 'none',
            padding: '10px 14px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
          onMouseOver={(e) => e.target.style.background = '#000000'}
          onMouseOut={(e) => e.target.style.background = '#111827'}
        >
          Support kontaktieren
        </button>
      </div>
    </div>
    </div>
  );
}